import { friendlyError } from '../../utils/userMessages';
import { brandPalette as palette } from '../../utils/brandPalette';
// src/components/dashboard/Reporting.jsx

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Paper,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    Avatar,
    Chip,
    Divider,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    LinearProgress,
    Alert,
    alpha,
    InputAdornment,
    CircularProgress,
    Switch,
    FormControlLabel,
    Tabs,
    Tab,
    Badge,
    Stack,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Collapse,
    Fade,
    Zoom,
} from '@mui/material';
import {
    Analytics as AnalyticsIcon,
    PictureAsPdf as PdfIcon,
    Download as DownloadIcon,
    Refresh as RefreshIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    People as PeopleIcon,
    Business as BusinessIcon,
    Event as EventIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Schedule as ScheduleIcon,
    Insights as TimelineIcon,
    BarChart as BarChartIcon,
    DonutLarge as PieChartIcon,
    CalendarToday as CalendarIcon,
    ArrowForward as ArrowForwardIcon,
    Download as FileDownloadIcon,
    Print as PrintIcon,
    Share as ShareIcon,
    Close as CloseIcon,
    Search as SearchIcon,
    FilterAltOff as FilterListIcon,
    CalendarToday as DateRangeIcon,
    FiberManualRecord as LiveIcon,
    AutoAwesome as AiIcon,
    Visibility as VisibilityIcon,
    Description as FileCopyIcon,
    Email as EmailIcon,
    Info as InfoIcon,
    Send as SendIcon,
    TableChart as TableChartIcon,
    Inventory2 as FolderOpenIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Close as ClearIcon,
    Dashboard as GridViewIcon,
    NotesOutlined as ViewListIcon,
    FilterAltOff as SortIcon,
    FilterAltOff as FilterAltIcon,
    Person as AccountCircleIcon,
    Group as GroupIcon,
    EventNote as MeetingRoomIcon,
    Email as MailIcon,
    Assessment as AssessmentIcon,
    ReceiptLong as ReceiptLongIcon,
    Stars as StarsIcon,
    Diamond as DiamondIcon,
    RocketLaunch as RocketLaunchIcon,
    Shield as ShieldIcon,
    Verified as VerifiedIcon,
    Storefront as StorefrontIcon,
    LocationOn as LocationOnIcon,
    Phone as PhoneIcon,
    Language as LanguageIcon,
    Public as PublicIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip as ChartTooltip,
    Legend,
    Filler,
} from 'chart.js';
import { Line, Bar, Pie, Doughnut } from 'react-chartjs-2';
import { useAuth } from '../auth/AuthContext';
import Header from '../common/Header/Header';
import LoadingSpinner from '../common/LoadingSpinner';
import { db } from '../../config/firebase';
import {
    collection,
    getDocs,
    query,
    orderBy,
    where,
    onSnapshot,
    Timestamp,
    doc,
    getDoc,
    limit,
    startAfter,
} from 'firebase/firestore';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';

// ============================================
// FIXED PDF IMPORTS
// ============================================
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    ChartTooltip,
    Legend,
    Filler
);

// ============================================
// DESIGN TOKENS - Premium Theme
// ============================================



const fontImport = `
@import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;0,9..144,700;1,9..144,500&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
`;

// ============================================
// ANIMATED BACKGROUND
// ============================================

const PremiumAnimatedBackground = () => (
    <Box
        aria-hidden="true"
        sx={{
            position: 'fixed',
            inset: 0,
            overflow: 'hidden',
            pointerEvents: 'none',
            zIndex: 0,
            background: `
                radial-gradient(circle at 4% 0%, rgba(var(--primary-rgb), .075), transparent 30rem),
                radial-gradient(circle at 96% 12%, rgba(var(--secondary-rgb), .055), transparent 32rem),
                linear-gradient(180deg, #F1F6F9 0%, #F1F6F9 48%, #EAF2F6 100%)
            `,
            '&::after': {
                content: '""',
                position: 'absolute',
                inset: 0,
                opacity: .28,
                backgroundImage: `linear-gradient(rgba(31,62,82,.014) 1px, transparent 1px), linear-gradient(90deg, rgba(31,62,82,.014) 1px, transparent 1px)`,
                backgroundSize: '38px 38px',
            },
        }}
    />
);

const AnimatedSection = ({ children, delay = 0 }) => (
    <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
        style={{ height: 'auto', width: '100%' }}
    >
        {children}
    </motion.div>
);

const SectionRule = ({ color = palette.rust, width = 40 }) => (
    <Box sx={{ 
        width, 
        height: 3, 
        borderRadius: 2, 
        background: `linear-gradient(90deg, ${color}, ${color}40, transparent)`, 
        mt: 0.75 
    }} />
);

// ============================================
// SECTION DIVIDER
// ============================================

const SectionDivider = ({ label, icon, color = palette.rust }) => (
    <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2, 
        my: 4,
        position: 'relative',
    }}>
        <Divider sx={{ 
            flex: 1, 
            borderColor: alpha(color, 0.15),
            '&::before': {
                borderColor: alpha(color, 0.15),
            }
        }} />
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 3,
            py: 1,
            borderRadius: '12px',
            bgcolor: alpha(color, 0.06),
            border: `1px solid ${alpha(color, 0.10)}`,
            backdropFilter: 'blur(8px)',
        }}>
            {icon && React.cloneElement(icon, { 
                sx: { fontSize: 20, color: color } 
            })}
            <Typography sx={{
                fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                fontWeight: 700,
                fontSize: '0.9rem',
                color: color,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
            }}>
                {label}
            </Typography>
            <Box sx={{
                width: 4,
                height: 4,
                borderRadius: '50%',
                bgcolor: color,
                opacity: 0.4,
            }} />
            <Typography sx={{
                fontSize: '0.7rem',
                color: palette.inkSoft,
                fontWeight: 500,
            }}>
                Analytics Overview
            </Typography>
        </Box>
        <Divider sx={{ 
            flex: 1, 
            borderColor: alpha(color, 0.15),
            '&::after': {
                borderColor: alpha(color, 0.15),
            }
        }} />
    </Box>
);

// ============================================
// PREMIUM STAT CARD
// ============================================

const PremiumStatCard = ({ title, value, icon, color, gradient, delay, change, trend }) => {
    const nodeRef = useRef(null);
    const hasRun = useRef(false);

    useEffect(() => {
        if (typeof value === 'number' && value > 0) {
            const node = nodeRef.current;
            if (!node || hasRun.current) return;

            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting && !hasRun.current) {
                        hasRun.current = true;
                        let current = 0;
                        const step = Math.ceil(value / 40);
                        const interval = setInterval(() => {
                            current += step;
                            if (current >= value) {
                                current = value;
                                clearInterval(interval);
                            }
                            if (node) node.textContent = Math.round(current).toLocaleString();
                        }, 25);
                        return () => clearInterval(interval);
                    }
                },
                { threshold: 0.3 }
            );

            observer.observe(node);
            return () => observer.disconnect();
        }
    }, [value]);

    return (
        <AnimatedSection delay={delay}>
            <motion.div
                whileHover={{ 
                    y: -8,
                    transition: { type: 'spring', stiffness: 400, damping: 17 }
                }}
                style={{ height: 'auto' }}
            >
                <Card
                    elevation={4}
                    sx={{
                        borderRadius: '20px',
                        border: `1px solid ${color}25`,
                        boxShadow: `
                            0 4px 24px ${alpha(color, 0.08)},
                            0 2px 8px rgba(25,57,78,0.04),
                            0 8px 32px ${alpha(color, 0.04)}
                        `,
                        transition: 'all 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
                        height: 'auto',
                        minHeight: 104,
                        display: 'flex',
                        flexDirection: 'column',
                        background: `linear-gradient(145deg, ${palette.paper}, ${alpha(color, 0.03)})`,
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                            boxShadow: `
                                0 12px 48px ${alpha(color, 0.16)},
                                0 4px 12px rgba(25,57,78,0.06),
                                0 16px 56px ${alpha(color, 0.08)}
                            `,
                            borderColor: `${color}50`,
                            transform: 'translateY(-6px)',
                            '& .stat-glow': {
                                opacity: 0.8,
                            },
                            '& .stat-particle': {
                                opacity: 0.5,
                            }
                        }
                    }}
                >
                    <Box
                        className="stat-glow"
                        sx={{
                            position: 'absolute',
                            top: -60,
                            right: -60,
                            width: 180,
                            height: 180,
                            borderRadius: '50%',
                            background: `radial-gradient(circle, ${alpha(color, 0.15)}, transparent 70%)`,
                            filter: 'blur(40px)',
                            opacity: 0.3,
                            transition: 'opacity 0.6s ease',
                            pointerEvents: 'none',
                        }}
                    />
                    <Box
                        className="stat-glow"
                        sx={{
                            position: 'absolute',
                            bottom: -40,
                            left: -40,
                            width: 140,
                            height: 140,
                            borderRadius: '50%',
                            background: `radial-gradient(circle, ${alpha(color, 0.10)}, transparent 70%)`,
                            filter: 'blur(30px)',
                            opacity: 0.2,
                            transition: 'opacity 0.6s ease',
                            pointerEvents: 'none',
                        }}
                    />
                    {[...Array(6)].map((_, i) => (
                        <motion.div
                            key={i}
                            className="stat-particle"
                            animate={{
                                y: ['0%', `${-30 - i * 10}%`, '0%'],
                                x: [`${i * 20}%`, `${i * 10}%`, `${i * 20}%`],
                                opacity: [0, 0.3, 0],
                            }}
                            transition={{
                                duration: 4 + i * 0.5,
                                repeat: Infinity,
                                ease: 'easeInOut',
                                delay: i * 0.3,
                            }}
                            style={{
                                position: 'absolute',
                                width: 2 + i * 0.5,
                                height: 2 + i * 0.5,
                                borderRadius: '50%',
                                background: color,
                                opacity: 0,
                                left: `${10 + i * 15}%`,
                                top: `${60 + i * 8}%`,
                                pointerEvents: 'none',
                            }}
                        />
                    ))}
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            opacity: 0.04,
                            backgroundImage: `radial-gradient(${color} 0.5px, transparent 0.5px)`,
                            backgroundSize: '16px 16px',
                            maskImage: 'radial-gradient(ellipse at 70% 30%, black 30%, transparent 70%)',
                            WebkitMaskImage: 'radial-gradient(ellipse at 70% 30%, black 30%, transparent 70%)',
                            pointerEvents: 'none',
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '3px',
                            background: `linear-gradient(90deg, ${color}, ${alpha(color, 0.3)}, transparent)`,
                            opacity: 0.6,
                        }}
                    />
                    <CardContent sx={{ 
                        p: 3, 
                        flex: 1, 
                        display: 'flex', 
                        flexDirection: 'column', 
                        position: 'relative',
                        zIndex: 1,
                    }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                            <Box sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: gradient,
                                color: '#fff',
                                boxShadow: `0 4px 20px ${alpha(color, 0.3)}`,
                                border: `1px solid ${alpha('#fff', 0.15)}`,
                                position: 'relative',
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    inset: 0,
                                    borderRadius: '12px',
                                    border: '1px solid rgba(255,255,255,0.15)',
                                    pointerEvents: 'none',
                                }
                            }}>
                                {React.cloneElement(icon, { sx: { fontSize: 20 } })}
                            </Box>
                            {change && (
                                <Chip
                                    label={change}
                                    size="small"
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: '0.55rem',
                                        height: 22,
                                        bgcolor: trend === 'up' ? `${alpha(palette.pine, 0.10)}` : `${alpha(palette.clay, 0.10)}`,
                                        color: trend === 'up' ? palette.pine : palette.clay,
                                        border: `1px solid ${trend === 'up' ? alpha(palette.pine, 0.06) : alpha(palette.clay, 0.06)}`,
                                    }}
                                    icon={trend === 'up' ? <TrendingUpIcon sx={{ fontSize: 13 }} /> : <TrendingDownIcon sx={{ fontSize: 13 }} />}
                                />
                            )}
                        </Box>
                        <Typography sx={{ 
                            fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", 
                            fontWeight: 700, 
                            color: palette.ink, 
                            fontSize: '2.4rem', 
                            lineHeight: 1.1,
                            letterSpacing: '-0.02em',
                            background: `linear-gradient(145deg, ${palette.ink}, ${color})`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            display: 'inline-block',
                            mb: 0.25,
                        }}>
                            <span ref={nodeRef}>0</span>
                        </Typography>
                        <Typography sx={{ 
                            color: palette.inkSoft, 
                            fontWeight: 600, 
                            textTransform: 'uppercase', 
                            letterSpacing: '0.08em', 
                            fontSize: '0.65rem',
                        }}>
                            {title}
                        </Typography>
                    </CardContent>
                </Card>
            </motion.div>
        </AnimatedSection>
    );
};

// ============================================
// TAB PANEL
// ============================================

function TabPanel({ children, value, index, ...other }) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`report-tabpanel-${index}`}
            aria-labelledby={`report-tab-${index}`}
            {...other}
            style={{ width: '100%' }}
        >
            {value === index && (
                <Box sx={{ pt: 3, width: '100%' }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

// ============================================
// MAIN REPORTING COMPONENT
// ============================================

const Reporting = () => {
    const navigate = useNavigate();
    const { currentUser, userData, companyData } = useAuth();
    const [loading, setLoading] = useState(true);
    const [generatingReport, setGeneratingReport] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const [reportData, setReportData] = useState(null);
    const [downloadFormat, setDownloadFormat] = useState('pdf');
    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const [emailDialogOpen, setEmailDialogOpen] = useState(false);
    const [emailAddress, setEmailAddress] = useState('');
    const [reportName, setReportName] = useState('TELCOFAIR Report');
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [users, setUsers] = useState([]);
    const [meetings, setMeetings] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [invitations, setInvitations] = useState([]);
    const [userStats, setUserStats] = useState({
        total: 0,
        active: 0,
        verified: 0,
        blocked: 0,
        newThisMonth: 0,
        growthRate: 0,
    });
    const [meetingStats, setMeetingStats] = useState({
        total: 0,
        completed: 0,
        pending: 0,
        cancelled: 0,
        completionRate: 0,
        monthlyTrend: [],
    });
    const [invitationStats, setInvitationStats] = useState({
        total: 0,
        accepted: 0,
        pending: 0,
        declined: 0,
        acceptanceRate: 0,
    });
    const [companyStats, setCompanyStats] = useState({
        total: 0,
        active: 0,
        verified: 0,
        withMeetings: 0,
        engagementRate: 0,
    });
    const [recentActivity, setRecentActivity] = useState([]);
    const [monthlyUserData, setMonthlyUserData] = useState(Array(12).fill(0));
    const [monthlyMeetingData, setMonthlyMeetingData] = useState(Array(12).fill(0));
    const [monthlyInvitationData, setMonthlyInvitationData] = useState(Array(12).fill(0));

    // Get the current company ID
    const getCompanyId = () => {
        return companyData?.id || companyData?.companyId || userData?.companyId || userData?.id || currentUser?.uid;
    };

    const getCompanyName = () => {
        return companyData?.companyName || userData?.companyName || companyData?.name || 'Your Company';
    };

    const companyId = getCompanyId();

    const fetchAllData = async () => {
        setLoading(true);
        try {
            if (!companyId) {
                console.warn('No company ID found');
                setLoading(false);
                return;
            }

            // Fetch users for this company only
            const usersRef = collection(db, 'users');
            const usersQuery = query(usersRef, where('companyId', '==', companyId));
            const usersSnapshot = await getDocs(usersQuery);
            const usersList = [];
            let activeUsers = 0, verifiedUsers = 0, blockedUsers = 0;
            const now = new Date();
            const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
            
            usersSnapshot.forEach((doc) => {
                const data = doc.data();
                if (doc.id !== '_schema' && doc.id !== '_placeholder') {
                    const user = { 
                        id: doc.id, 
                        ...data,
                        associatedCompanyName: companyData?.companyName || data.companyName || 'N/A',
                    };
                    usersList.push(user);
                    if (data.isActive && !data.isBlocked) activeUsers++;
                    if (data.isVerified) verifiedUsers++;
                    if (data.isBlocked) blockedUsers++;
                }
            });
            setUsers(usersList);

            // Fetch meetings for this company
            const meetingsRef = collection(db, 'meetings');
            const meetingsQuery = query(
                meetingsRef, 
                where('companyId', '==', companyId)
            );
            const meetingsSnapshot = await getDocs(meetingsQuery);
            const meetingsList = [];
            let completed = 0, pending = 0, cancelled = 0;
            meetingsSnapshot.forEach((doc) => {
                const data = doc.data();
                if (doc.id !== '_schema' && doc.id !== '_placeholder') {
                    const meeting = { id: doc.id, ...data };
                    meetingsList.push(meeting);
                    if (data.status === 'completed') completed++;
                    else if (data.status === 'pending') pending++;
                    else if (data.status === 'cancelled') cancelled++;
                }
            });
            setMeetings(meetingsList);

            // Fetch invitations for this company
            const invitationsRef = collection(db, 'invitations');
            const invitationsQuery = query(
                invitationsRef,
                where('companyId', '==', companyId)
            );
            const invitationsSnapshot = await getDocs(invitationsQuery);
            const invitationsList = [];
            let accepted = 0, pendingInv = 0, declined = 0;
            invitationsSnapshot.forEach((doc) => {
                const data = doc.data();
                if (doc.id !== '_schema' && doc.id !== '_placeholder') {
                    const inv = { id: doc.id, ...data };
                    invitationsList.push(inv);
                    if (data.status === 'accepted') accepted++;
                    else if (data.status === 'pending') pendingInv++;
                    else if (data.status === 'declined') declined++;
                }
            });
            setInvitations(invitationsList);

            // Fetch company details
            const companyDoc = await getDoc(doc(db, 'companies', companyId));
            let companyDataObj = null;
            if (companyDoc.exists()) {
                companyDataObj = { id: companyDoc.id, ...companyDoc.data() };
            }

            const totalUsers = usersList.length;
            const newThisMonth = usersList.filter(u => {
                const createdAt = u.createdAt?.toDate?.() || new Date(u.createdAt);
                return createdAt >= monthStart;
            }).length;
            
            setUserStats({
                total: totalUsers,
                active: activeUsers,
                verified: verifiedUsers,
                blocked: blockedUsers,
                newThisMonth,
                growthRate: totalUsers > 0 ? Math.round((newThisMonth / totalUsers) * 100) : 0,
            });

            const totalMeetings = meetingsList.length;
            const completionRate = totalMeetings > 0 ? Math.round((completed / totalMeetings) * 100) : 0;
            setMeetingStats({
                total: totalMeetings,
                completed,
                pending,
                cancelled,
                completionRate,
                monthlyTrend: monthlyMeetingData,
            });

            const totalInv = invitationsList.length;
            const acceptanceRate = totalInv > 0 ? Math.round((accepted / totalInv) * 100) : 0;
            setInvitationStats({
                total: totalInv,
                accepted,
                pending: pendingInv,
                declined,
                acceptanceRate,
            });

            // Company stats - single company
            const isActive = companyDataObj?.isActive && !companyDataObj?.isBlocked;
            const isVerified = companyDataObj?.isVerified || false;
            const hasMeetings = meetingsList.length > 0;
            
            setCompanyStats({
                total: 1,
                active: isActive ? 1 : 0,
                verified: isVerified ? 1 : 0,
                withMeetings: hasMeetings ? 1 : 0,
                engagementRate: hasMeetings ? 100 : 0,
            });

            // Monthly data
            const userCounts = Array(12).fill(0);
            const meetingCounts = Array(12).fill(0);
            const invitationCounts = Array(12).fill(0);

            usersList.forEach(u => {
                const date = u.createdAt?.toDate?.() || new Date(u.createdAt);
                const month = date.getMonth();
                userCounts[month]++;
            });

            meetingsList.forEach(m => {
                const date = m.createdAt?.toDate?.() || new Date(m.createdAt);
                const month = date.getMonth();
                meetingCounts[month]++;
            });

            invitationsList.forEach(inv => {
                const date = inv.createdAt?.toDate?.() || new Date(inv.createdAt);
                const month = date.getMonth();
                invitationCounts[month]++;
            });

            setMonthlyUserData(userCounts);
            setMonthlyMeetingData(meetingCounts);
            setMonthlyInvitationData(invitationCounts);

            // Recent activity
            const activities = [];
            meetingsList.slice(0, 8).forEach(m => {
                activities.push({
                    id: m.id,
                    type: 'meeting',
                    title: m.title || 'Meeting',
                    status: m.status,
                    time: m.createdAt?.toDate?.() || new Date(),
                    user: m.requesterName || 'Unknown',
                });
            });
            invitationsList.slice(0, 8).forEach(inv => {
                activities.push({
                    id: inv.id,
                    type: 'invitation',
                    title: 'Invitation',
                    status: inv.status,
                    time: inv.createdAt?.toDate?.() || new Date(),
                    user: inv.companyName || 'Unknown',
                });
            });
            activities.sort((a, b) => b.time - a.time);
            setRecentActivity(activities.slice(0, 10));

            setReportData({
                users: usersList,
                meetings: meetingsList,
                companies: companyDataObj ? [companyDataObj] : [],
                invitations: invitationsList,
                userStats,
                meetingStats,
                invitationStats,
                companyStats,
                monthlyUserData: userCounts,
                monthlyMeetingData: meetingCounts,
                monthlyInvitationData: invitationCounts,
                recentActivity: activities.slice(0, 10),
                generatedAt: new Date(),
                companyName: getCompanyName(),
            });

        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load report data');
        } finally {
            setLoading(false);
        }
    };

    // ============================================
    // FIXED PDF GENERATION
    // ============================================

    const generatePDFReport = async () => {
        if (!reportData) {
            toast.error('No report data available');
            return;
        }

        setGeneratingReport(true);
        try {
            // Create new PDF document
            const doc = new jsPDF('p', 'mm', 'a4');
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            let y = 20;

            // Header
            doc.setFillColor(30, 23, 18);
            doc.rect(0, 0, pageWidth, 60, 'F');
            
            doc.setTextColor(255, 255, 255);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(32);
            doc.text('TELCOFAIR', 20, 30);
            
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(16);
            doc.setTextColor(196, 90, 38);
            doc.text(`${getCompanyName()} - Analytics Report`, 20, 48);

            const today = new Date().toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
            doc.setFontSize(9);
            doc.setTextColor(180, 180, 180);
            doc.text(`Generated: ${today}`, pageWidth - 50, 30);
            doc.text(`Company: ${getCompanyName()}`, pageWidth - 50, 42);
            doc.text(`ID: ${Math.random().toString(36).substring(2, 8).toUpperCase()}`, pageWidth - 50, 54);

            y = 78;

            // Executive Summary
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(18);
            doc.setTextColor(30, 23, 18);
            doc.text(`Executive Summary - ${getCompanyName()}`, 20, y);
            y += 10;

            const stats = [
                `Total Users: ${userStats.total} (${userStats.active} active, ${userStats.verified} verified)`,
                `Total Meetings: ${meetingStats.total} (${meetingStats.completed} completed)`,
                `Total Invitations: ${invitationStats.total} (${invitationStats.accepted} accepted)`,
                `Company Status: ${companyStats.active > 0 ? 'Active' : 'Inactive'}`,
                `Engagement Rate: ${companyStats.engagementRate}% | Acceptance Rate: ${invitationStats.acceptanceRate}%`,
            ];

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            doc.setTextColor(95, 83, 71);
            stats.forEach((text, i) => {
                doc.text(`• ${text}`, 20, y + 10 + (i * 7));
            });
            y += 10 + stats.length * 7 + 12;

            // Tab specific content using autoTable
            if (tabValue === 0) {
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(16);
                doc.setTextColor(30, 23, 18);
                doc.text('User Analytics', 20, y);
                y += 10;

                const userData = [
                    ['Metric', 'Value'],
                    ['Total Users', userStats.total],
                    ['Active Users', userStats.active],
                    ['Verified Users', userStats.verified],
                    ['Blocked Users', userStats.blocked],
                    ['New This Month', userStats.newThisMonth],
                    ['Growth Rate', `${userStats.growthRate}%`],
                ];

                autoTable(doc, {
                    startY: y,
                    head: [userData[0]],
                    body: userData.slice(1),
                    theme: 'grid',
                    headStyles: {
                        fillColor: [106, 27, 154],
                        textColor: [255, 255, 255],
                        fontStyle: 'bold',
                        fontSize: 10,
                    },
                    alternateRowStyles: {
                        fillColor: [247, 242, 234],
                    },
                    styles: {
                        fontSize: 9,
                        cellPadding: 4,
                    },
                    margin: { left: 20, right: 20 },
                });
                y = doc.lastAutoTable.finalY + 12;

                if (y > pageHeight - 60) { doc.addPage(); y = 20; }
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(14);
                doc.setTextColor(30, 23, 18);
                doc.text('User List', 20, y);
                y += 8;

                const userRows = users.slice(0, 20).map(u => [
                    u.displayName || 'N/A',
                    u.email || 'N/A',
                    u.role || 'N/A',
                    u.isActive ? 'Active' : 'Inactive',
                ]);

                autoTable(doc, {
                    startY: y,
                    head: [['Name', 'Email', 'Role', 'Status']],
                    body: userRows,
                    theme: 'grid',
                    headStyles: {
                        fillColor: [42, 92, 69],
                        textColor: [255, 255, 255],
                        fontStyle: 'bold',
                        fontSize: 8,
                    },
                    alternateRowStyles: {
                        fillColor: [247, 242, 234],
                    },
                    styles: {
                        fontSize: 7.5,
                        cellPadding: 2,
                    },
                    margin: { left: 20, right: 20 },
                });
            }

            if (tabValue === 1) {
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(16);
                doc.setTextColor(30, 23, 18);
                doc.text('Meeting Analytics', 20, y);
                y += 10;

                const meetingData = [
                    ['Metric', 'Value'],
                    ['Total Meetings', meetingStats.total],
                    ['Completed', meetingStats.completed],
                    ['Pending', meetingStats.pending],
                    ['Cancelled', meetingStats.cancelled],
                    ['Completion Rate', `${meetingStats.completionRate}%`],
                ];

                autoTable(doc, {
                    startY: y,
                    head: [meetingData[0]],
                    body: meetingData.slice(1),
                    theme: 'grid',
                    headStyles: {
                        fillColor: [196, 90, 38],
                        textColor: [255, 255, 255],
                        fontStyle: 'bold',
                        fontSize: 10,
                    },
                    alternateRowStyles: {
                        fillColor: [247, 242, 234],
                    },
                    styles: {
                        fontSize: 9,
                        cellPadding: 4,
                    },
                    margin: { left: 20, right: 20 },
                });
                y = doc.lastAutoTable.finalY + 12;

                if (y > pageHeight - 60) { doc.addPage(); y = 20; }
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(14);
                doc.setTextColor(30, 23, 18);
                doc.text('Meeting List', 20, y);
                y += 8;

                const meetingRows = meetings.slice(0, 20).map(m => [
                    m.title || 'N/A',
                    m.status || 'N/A',
                    m.scheduledTime?.toDate?.()?.toLocaleDateString() || 'N/A',
                    m.duration || 'N/A',
                ]);

                autoTable(doc, {
                    startY: y,
                    head: [['Title', 'Status', 'Scheduled', 'Duration (min)']],
                    body: meetingRows,
                    theme: 'grid',
                    headStyles: {
                        fillColor: [184, 133, 42],
                        textColor: [255, 255, 255],
                        fontStyle: 'bold',
                        fontSize: 8,
                    },
                    alternateRowStyles: {
                        fillColor: [247, 242, 234],
                    },
                    styles: {
                        fontSize: 7.5,
                        cellPadding: 2,
                    },
                    margin: { left: 20, right: 20 },
                });
            }

            if (tabValue === 2) {
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(16);
                doc.setTextColor(30, 23, 18);
                doc.text('Invitation Analytics', 20, y);
                y += 10;

                const invData = [
                    ['Metric', 'Value'],
                    ['Total Invitations', invitationStats.total],
                    ['Accepted', invitationStats.accepted],
                    ['Pending', invitationStats.pending],
                    ['Declined', invitationStats.declined],
                    ['Acceptance Rate', `${invitationStats.acceptanceRate}%`],
                ];

                autoTable(doc, {
                    startY: y,
                    head: [invData[0]],
                    body: invData.slice(1),
                    theme: 'grid',
                    headStyles: {
                        fillColor: [26, 107, 115],
                        textColor: [255, 255, 255],
                        fontStyle: 'bold',
                        fontSize: 10,
                    },
                    alternateRowStyles: {
                        fillColor: [247, 242, 234],
                    },
                    styles: {
                        fontSize: 9,
                        cellPadding: 4,
                    },
                    margin: { left: 20, right: 20 },
                });
                y = doc.lastAutoTable.finalY + 12;

                if (y > pageHeight - 60) { doc.addPage(); y = 20; }
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(14);
                doc.setTextColor(30, 23, 18);
                doc.text('Invitation List', 20, y);
                y += 8;

                const invRows = invitations.slice(0, 20).map(inv => [
                    inv.companyName || 'N/A',
                    inv.targetCompanyName || 'N/A',
                    inv.status || 'N/A',
                    `${Math.round(inv.aiScore || 0)}%`,
                    inv.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A',
                ]);

                autoTable(doc, {
                    startY: y,
                    head: [['From', 'To', 'Status', 'AI Score', 'Sent']],
                    body: invRows,
                    theme: 'grid',
                    headStyles: {
                        fillColor: [26, 107, 115],
                        textColor: [255, 255, 255],
                        fontStyle: 'bold',
                        fontSize: 8,
                    },
                    alternateRowStyles: {
                        fillColor: [247, 242, 234],
                    },
                    styles: {
                        fontSize: 7.5,
                        cellPadding: 2,
                    },
                    margin: { left: 20, right: 20 },
                });
            }

            if (tabValue === 3) {
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(16);
                doc.setTextColor(30, 23, 18);
                doc.text('Company Analytics', 20, y);
                y += 10;

                const companyData = [
                    ['Metric', 'Value'],
                    ['Company Name', getCompanyName()],
                    ['Status', companyStats.active > 0 ? 'Active' : 'Inactive'],
                    ['Verified', companyStats.verified > 0 ? 'Yes' : 'No'],
                    ['Total Users', userStats.total],
                    ['Total Meetings', meetingStats.total],
                    ['Total Invitations', invitationStats.total],
                ];

                autoTable(doc, {
                    startY: y,
                    head: [companyData[0]],
                    body: companyData.slice(1),
                    theme: 'grid',
                    headStyles: {
                        fillColor: [30, 23, 18],
                        textColor: [255, 255, 255],
                        fontStyle: 'bold',
                        fontSize: 10,
                    },
                    alternateRowStyles: {
                        fillColor: [247, 242, 234],
                    },
                    styles: {
                        fontSize: 9,
                        cellPadding: 4,
                    },
                    margin: { left: 20, right: 20 },
                });
            }

            // Footer
            const pageCount = doc.internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.setFontSize(8);
                doc.setTextColor(143, 130, 115);
                doc.text(`Page ${i} of ${pageCount}`, pageWidth - 30, pageHeight - 10);
                doc.text(`Confidential - ${getCompanyName()} - TELCOFAIR Analytics`, 20, pageHeight - 10);
            }

            const fileName = `TELCOFAIR_${getCompanyName().replace(/\s/g, '_')}_${['Users', 'Meetings', 'Invitations', 'Companies'][tabValue]}_Report_${new Date().toISOString().split('T')[0]}.pdf`;
            doc.save(fileName);
            toast.success('PDF report downloaded successfully!');

        } catch (error) {
            console.error('Error generating PDF:', error);
            toast.error(friendlyError(error, 'Failed to generate PDF report.'));
        } finally {
            setGeneratingReport(false);
            setExportDialogOpen(false);
        }
    };

    const generateExcelReport = () => {
        try {
            const wb = XLSX.utils.book_new();
            
            const userRows = [
                [`${getCompanyName()} - User Report`],
                [`Generated: ${new Date().toLocaleString()}`],
                [],
                ['Name', 'Email', 'Role', 'Status', 'Verified', 'Joined']
            ];
            users.forEach(u => {
                userRows.push([
                    u.displayName || 'N/A',
                    u.email || 'N/A',
                    u.role || 'N/A',
                    u.isActive && !u.isBlocked ? 'Active' : 'Inactive',
                    u.isVerified ? 'Yes' : 'No',
                    u.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'
                ]);
            });
            const userWs = XLSX.utils.aoa_to_sheet(userRows);
            XLSX.utils.book_append_sheet(wb, userWs, 'Users');

            const meetingRows = [
                [`${getCompanyName()} - Meeting Report`],
                [`Generated: ${new Date().toLocaleString()}`],
                [],
                ['Title', 'Status', 'Scheduled', 'Duration', 'Participants']
            ];
            meetings.forEach(m => {
                meetingRows.push([
                    m.title || 'N/A',
                    m.status || 'N/A',
                    m.scheduledTime?.toDate?.()?.toLocaleDateString() || 'N/A',
                    m.duration || 'N/A',
                    m.participants?.length || 0
                ]);
            });
            const meetingWs = XLSX.utils.aoa_to_sheet(meetingRows);
            XLSX.utils.book_append_sheet(wb, meetingWs, 'Meetings');

            const invRows = [
                [`${getCompanyName()} - Invitation Report`],
                [`Generated: ${new Date().toLocaleString()}`],
                [],
                ['From', 'To', 'Status', 'AI Score', 'Sent']
            ];
            invitations.forEach(inv => {
                invRows.push([
                    inv.companyName || 'N/A',
                    inv.targetCompanyName || 'N/A',
                    inv.status || 'N/A',
                    inv.aiScore || 'N/A',
                    inv.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'
                ]);
            });
            const invWs = XLSX.utils.aoa_to_sheet(invRows);
            XLSX.utils.book_append_sheet(wb, invWs, 'Invitations');

            const statsRows = [
                [`${getCompanyName()} - Statistics Summary`],
                [],
                ['Metric', 'Value'],
                ['Company Name', getCompanyName()],
                ['Total Users', userStats.total],
                ['Active Users', userStats.active],
                ['Total Meetings', meetingStats.total],
                ['Completed Meetings', meetingStats.completed],
                ['Total Invitations', invitationStats.total],
                ['Accepted Invitations', invitationStats.accepted],
                ['Engagement Rate', `${companyStats.engagementRate}%`],
                ['Acceptance Rate', `${invitationStats.acceptanceRate}%`],
            ];
            const statsWs = XLSX.utils.aoa_to_sheet(statsRows);
            XLSX.utils.book_append_sheet(wb, statsWs, 'Statistics');

            const fileName = `TELCOFAIR_${getCompanyName().replace(/\s/g, '_')}_Complete_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
            XLSX.writeFile(wb, fileName);
            toast.success('Excel report downloaded successfully!');
            setExportDialogOpen(false);

        } catch (error) {
            console.error('Error generating Excel:', error);
            toast.error('Failed to generate Excel report');
        }
    };

    // ============================================
    // CHART DATA
    // ============================================

    const userChartData = {
        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        datasets: [
            {
                label: 'New Users',
                data: monthlyUserData,
                borderColor: palette.purple,
                backgroundColor: `${palette.purple}15`,
                fill: true,
                tension: 0.4,
                pointBackgroundColor: palette.purple,
                pointBorderColor: '#fff',
                pointBorderWidth: 2,
                pointRadius: 5,
                borderWidth: 3,
            },
        ],
    };

    const meetingChartData = {
        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        datasets: [
            {
                label: 'Meetings',
                data: monthlyMeetingData,
                borderColor: palette.rust,
                backgroundColor: `${palette.rust}15`,
                fill: true,
                tension: 0.4,
                pointBackgroundColor: palette.rust,
                pointBorderColor: '#fff',
                pointBorderWidth: 2,
                pointRadius: 5,
                borderWidth: 3,
            },
        ],
    };

    const invitationChartData = {
        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        datasets: [
            {
                label: 'Invitations',
                data: monthlyInvitationData,
                borderColor: palette.gold,
                backgroundColor: `${palette.gold}15`,
                fill: true,
                tension: 0.4,
                pointBackgroundColor: palette.gold,
                pointBorderColor: '#fff',
                pointBorderWidth: 2,
                pointRadius: 5,
                borderWidth: 3,
            },
        ],
    };

    const meetingStatusData = {
        labels: ['Completed', 'Pending', 'Cancelled'],
        datasets: [
            {
                data: [
                    meetingStats.completed || 1,
                    meetingStats.pending || 1,
                    meetingStats.cancelled || 1,
                ],
                backgroundColor: [palette.pine, palette.gold, palette.clay],
                borderColor: '#fff',
                borderWidth: 3,
            },
        ],
    };

    const invitationStatusData = {
        labels: ['Accepted', 'Pending', 'Declined'],
        datasets: [
            {
                data: [
                    invitationStats.accepted || 1,
                    invitationStats.pending || 1,
                    invitationStats.declined || 1,
                ],
                backgroundColor: [palette.pine, palette.gold, palette.clay],
                borderColor: '#fff',
                borderWidth: 3,
            },
        ],
    };

    const companyStatusData = {
        labels: ['Active', 'Inactive'],
        datasets: [
            {
                data: [
                    companyStats.active || 1,
                    (companyStats.total || 2) - (companyStats.active || 1),
                ],
                backgroundColor: [palette.pine, palette.inkFaint],
                borderColor: '#fff',
                borderWidth: 3,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    usePointStyle: true,
                    pointStyle: 'circle',
                    padding: 20,
                    font: {
                        family: "'Plus Jakarta Sans', sans-serif",
                        size: 12,
                        weight: 600,
                    },
                },
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: {
                    color: 'rgba(30,23,18,0.05)',
                    drawBorder: false,
                },
                ticks: {
                    font: {
                        family: "'Plus Jakarta Sans', sans-serif",
                        size: 10,
                    },
                    stepSize: 1,
                },
            },
            x: {
                grid: {
                    display: false,
                },
                ticks: {
                    font: {
                        family: "'Plus Jakarta Sans', sans-serif",
                        size: 10,
                    },
                },
            },
        },
    };

    const pieOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    usePointStyle: true,
                    pointStyle: 'circle',
                    padding: 16,
                    font: {
                        family: "'Plus Jakarta Sans', sans-serif",
                        size: 12,
                        weight: 600,
                    },
                },
            },
        },
    };

    const doughnutOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    usePointStyle: true,
                    pointStyle: 'circle',
                    padding: 16,
                    font: {
                        family: "'Plus Jakarta Sans', sans-serif",
                        size: 12,
                        weight: 600,
                    },
                },
            },
        },
        cutout: '60%',
    };

    // ============================================
    // USE EFFECT
    // ============================================

    useEffect(() => {
        if (companyId) {
            fetchAllData();

            // Set up real-time listeners
            const unsubscribeMeetings = onSnapshot(
                query(collection(db, 'meetings'), where('companyId', '==', companyId)),
                () => fetchAllData()
            );
            const unsubscribeInvitations = onSnapshot(
                query(collection(db, 'invitations'), where('companyId', '==', companyId)),
                () => fetchAllData()
            );
            const unsubscribeUsers = onSnapshot(
                query(collection(db, 'users'), where('companyId', '==', companyId)),
                () => fetchAllData()
            );
            
            return () => {
                unsubscribeMeetings();
                unsubscribeInvitations();
                unsubscribeUsers();
            };
        } else {
            setLoading(false);
        }
    }, [companyId]);

    // ============================================
    // RENDER
    // ============================================

    const canvasSx = {
        minHeight: '100vh',
        position: 'relative',
        bgcolor: palette.canvas,
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        overflowX: 'hidden',
    };

    if (loading) {
        return <LoadingSpinner label={`Loading reports for ${getCompanyName()}…`} />;
    }

    if (!companyId) {
        return (
            <Box className="erp-page-shell erp-internal-page tf-reporting-page" sx={canvasSx}>
                <style>{fontImport}</style>
                
                <Header />
                <Container maxWidth={false} disableGutters sx={{ py: 6, px: { xs: 2, sm: 4, md: 6, lg: 8 }, position: 'relative', width: '100%' }}>
                    <Paper
                        elevation={4}
                        sx={{
                            borderRadius: '24px',
                            border: `1px solid ${palette.hairlineStrong}`,
                            boxShadow: `
                                0 4px 24px rgba(25,57,78,0.06),
                                0 2px 8px rgba(30,23,18,0.03)
                            `,
                            background: palette.paper,
                            p: 6,
                            textAlign: 'center',
                        }}
                    >
                        <BusinessIcon sx={{ fontSize: 64, color: palette.inkFaint, opacity: 0.3 }} />
                        <Typography variant="h5" sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 700, color: palette.ink, mt: 2 }}>
                            No Company Found
                        </Typography>
                        <Typography sx={{ color: palette.inkSoft, mt: 1 }}>
                            Please complete your company profile to access analytics and reporting.
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={() => navigate('/dashboard/company')}
                            sx={{
                                mt: 3,
                                borderRadius: '12px',
                                textTransform: 'none',
                                fontWeight: 600,
                                background: `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`,
                                boxShadow: '0 4px 16px rgba(196,90,38,0.3)',
                                '&:hover': {
                                    boxShadow: '0 8px 24px rgba(196,90,38,0.4)',
                                    transform: 'translateY(-2px)',
                                }
                            }}
                        >
                            Go to Company Profile
                        </Button>
                    </Paper>
                </Container>
            </Box>
        );
    }

    const tabIcons = [
        { icon: <PeopleIcon />, label: 'Users', color: palette.purple },
        { icon: <MeetingRoomIcon />, label: 'Meetings', color: palette.rust },
        { icon: <MailIcon />, label: 'Invitations', color: palette.gold },
        { icon: <BusinessIcon />, label: 'Company', color: palette.pine },
    ];

    return (
        <Box className="erp-page-shell erp-internal-page tf-reporting-page" sx={canvasSx}>
            <style>{fontImport}</style>
            <style>
                {`
                    @keyframes pulse {
                        0%, 100% { opacity: 1; transform: scale(1); }
                        50% { opacity: 0.5; transform: scale(0.8); }
                    }
                    @keyframes shimmerEdge {
                        0% { background-position: 0% 0%; }
                        100% { background-position: 300% 0%; }
                    }
                    @keyframes float {
                        0%, 100% { transform: translateY(0px); }
                        50% { transform: translateY(-10px); }
                    }
                    @keyframes shimmerRule {
                        0%, 100% { opacity: 0.7; width: 40px; }
                        50% { opacity: 1; width: 56px; }
                    }
                `}
            </style>
            
            <Header />

            <Container maxWidth={false} disableGutters sx={{ py: { xs: 1.75, md: 2.25 }, px: { xs: 1.5, sm: 2, md: 2.5, lg: 3 }, position: 'relative', width: '100%', zIndex: 1 }}>
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                    <Box className="erp-page-hero" sx={{ mb: 2.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{
                                    width: 56,
                                    height: 56,
                                    borderRadius: '16px',
                                    background: `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#fff',
                                    boxShadow: '0 4px 20px rgba(196,90,38,0.3)',
                                }}>
                                    <AssessmentIcon sx={{ fontSize: 32 }} />
                                </Box>
                                <Box>
                                    <Typography sx={{
                                        fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                                        fontWeight: 700,
                                        fontSize: { xs: '1.8rem', md: '2.4rem' },
                                        color: palette.ink,
                                        letterSpacing: '-0.02em',
                                        lineHeight: 1.15,
                                    }}>
                                        Reporting & Analytics
                                    </Typography>
                                    <Typography sx={{ color: palette.inkSoft, fontSize: '0.95rem', mt: 0.25 }}>
                                        Analytics and performance metrics for <strong>{getCompanyName()}</strong>
                                    </Typography>
                                    <SectionRule color={palette.rust} />
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                                <Chip
                                    icon={<LiveIcon sx={{ fontSize: 10, color: `${palette.pine} !important` }} />}
                                    label={`Live • ${new Date().toLocaleTimeString()}`}
                                    sx={{
                                        bgcolor: alpha(palette.pine, 0.08),
                                        color: palette.pine,
                                        fontWeight: 600,
                                        fontSize: '0.7rem',
                                        height: 32,
                                        borderRadius: '16px',
                                        border: `1px solid ${alpha(palette.pine, 0.15)}`,
                                    }}
                                />
                                <Chip
                                    icon={<BusinessIcon sx={{ fontSize: 14 }} />}
                                    label={getCompanyName()}
                                    sx={{
                                        bgcolor: alpha(palette.rust, 0.08),
                                        color: palette.rust,
                                        fontWeight: 600,
                                        fontSize: '0.7rem',
                                        height: 32,
                                        borderRadius: '16px',
                                        border: `1px solid ${alpha(palette.rust, 0.15)}`,
                                    }}
                                />
                                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<RefreshIcon />}
                                        onClick={fetchAllData}
                                        sx={{
                                            borderRadius: '12px',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: '0.75rem',
                                            borderColor: palette.hairlineStrong,
                                            color: palette.ink,
                                            '&:hover': {
                                                borderColor: palette.rust,
                                                color: palette.rust,
                                                bgcolor: alpha(palette.rust, 0.04),
                                                transform: 'translateY(-2px)',
                                            }
                                        }}
                                    >
                                        Refresh
                                    </Button>
                                </motion.div>
                                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                    <Button
                                        variant="contained"
                                        startIcon={<FileDownloadIcon />}
                                        onClick={() => setExportDialogOpen(true)}
                                        sx={{
                                            borderRadius: '12px',
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            fontSize: '0.8rem',
                                            py: 0.8,
                                            px: 3,
                                            background: `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`,
                                            boxShadow: '0 4px 16px rgba(196,90,38,0.3)',
                                            '&:hover': {
                                                boxShadow: '0 8px 24px rgba(196,90,38,0.4)',
                                                transform: 'translateY(-2px)',
                                            }
                                        }}
                                    >
                                        Export Report
                                    </Button>
                                </motion.div>
                            </Box>
                        </Box>
                    </Box>
                </motion.div>

                {/* Full Width Tabs */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1, duration: 0.5 }}
                >
                    <Paper
                        elevation={4}
                        sx={{
                            borderRadius: '24px',
                            border: `1px solid ${palette.hairlineStrong}`,
                            boxShadow: `
                                0 4px 24px rgba(25,57,78,0.06),
                                0 2px 8px rgba(30,23,18,0.03),
                                0 8px 40px rgba(25,57,78,0.04)
                            `,
                            background: palette.paper,
                            position: 'relative',
                            overflow: 'hidden',
                            width: '100%',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: '2px',
                                background: `linear-gradient(90deg, ${palette.rust}60, ${palette.gold}60, ${palette.pine}60, ${palette.purple}60, ${palette.rust}60)`,
                                backgroundSize: '300% 100%',
                                opacity: 0.4,
                                animation: 'shimmerEdge 12s linear infinite',
                                pointerEvents: 'none',
                                borderRadius: '24px 24px 0 0',
                            },
                            '&:hover': {
                                boxShadow: `
                                    0 8px 32px rgba(30,23,18,0.08),
                                    0 4px 12px rgba(25,57,78,0.04),
                                    0 16px 56px rgba(25,57,78,0.06)
                                `,
                                transition: 'all 0.3s ease',
                            }
                        }}
                    >
                        <Tabs
                            value={tabValue}
                            onChange={(e, v) => setTabValue(v)}
                            variant="fullWidth"
                            sx={{
                                borderBottom: `1px solid ${palette.hairline}`,
                                bgcolor: palette.canvasAlt,
                                '& .MuiTab-root': {
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    fontSize: '0.85rem',
                                    minHeight: 64,
                                    py: 1.5,
                                    color: palette.inkSoft,
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        color: palette.ink,
                                        bgcolor: alpha(palette.rust, 0.02),
                                    },
                                    '&.Mui-selected': {
                                        color: palette.rust,
                                        background: `linear-gradient(180deg, ${alpha(palette.rust, 0.04)}, transparent)`,
                                    },
                                },
                                '& .MuiTabs-indicator': {
                                    backgroundColor: palette.rust,
                                    height: 3,
                                    borderRadius: '3px 3px 0 0',
                                },
                            }}
                        >
                            {tabIcons.map((tab, index) => (
                                <Tab
                                    key={index}
                                    icon={
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 36,
                                            height: 36,
                                            borderRadius: '10px',
                                            bgcolor: tabValue === index ? alpha(tab.color, 0.12) : 'transparent',
                                            color: tabValue === index ? tab.color : palette.inkFaint,
                                            transition: 'all 0.3s ease',
                                            mb: 0.5,
                                        }}>
                                            {React.cloneElement(tab.icon, { 
                                                sx: { 
                                                    fontSize: 22,
                                                    color: tabValue === index ? tab.color : palette.inkFaint,
                                                } 
                                            })}
                                        </Box>
                                    }
                                    label={
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography sx={{
                                                fontWeight: tabValue === index ? 700 : 600,
                                                fontSize: '0.85rem',
                                                color: tabValue === index ? palette.ink : palette.inkSoft,
                                            }}>
                                                {tab.label}
                                            </Typography>
                                            {tabValue === index && (
                                                <Box sx={{
                                                    width: 20,
                                                    height: 2,
                                                    borderRadius: 1,
                                                    bgcolor: tab.color,
                                                    mx: 'auto',
                                                    mt: 0.5,
                                                }} />
                                            )}
                                        </Box>
                                    }
                                    sx={{
                                        '& .MuiTab-iconWrapper': {
                                            marginBottom: '4px',
                                        }
                                    }}
                                />
                            ))}
                        </Tabs>

                        {/* ==================== USERS TAB ==================== */}
                        <TabPanel value={tabValue} index={0}>
                            <Box sx={{ p: { xs: 2, md: 3.5 } }}>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                        <Button
                                            variant="contained"
                                            startIcon={<FileDownloadIcon />}
                                            onClick={() => setExportDialogOpen(true)}
                                            sx={{
                                                borderRadius: '12px',
                                                textTransform: 'none',
                                                fontWeight: 700,
                                                fontSize: '0.8rem',
                                                py: 0.8,
                                                px: 3,
                                                background: `linear-gradient(135deg, ${palette.purple}, ${palette.navy})`,
                                                boxShadow: `0 4px 16px ${alpha(palette.purple, 0.3)}`,
                                                '&:hover': {
                                                    boxShadow: `0 8px 24px ${alpha(palette.purple, 0.4)}`,
                                                    transform: 'translateY(-2px)',
                                                }
                                            }}
                                        >
                                            Generate User Report
                                        </Button>
                                    </motion.div>
                                </Box>

                                <SectionDivider 
                                    label="User Overview" 
                                    icon={<PeopleIcon />} 
                                    color={palette.purple} 
                                />

                                <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr 1fr' },
                                    gap: 2.5,
                                    mb: 4,
                                }}>
                                    <PremiumStatCard
                                        title="Total Users"
                                        value={userStats.total}
                                        icon={<PeopleIcon />}
                                        color={palette.purple}
                                        gradient={`linear-gradient(135deg, ${palette.purple}, ${palette.navy})`}
                                        delay={0.03}
                                        change={userStats.total > 0 ? "+12%" : "0%"}
                                        trend="up"
                                    />
                                    <PremiumStatCard
                                        title="Active Users"
                                        value={userStats.active}
                                        icon={<AccountCircleIcon />}
                                        color={palette.pine}
                                        gradient={`linear-gradient(135deg, ${palette.pine}, ${palette.pineDeep})`}
                                        delay={0.08}
                                        change={userStats.active > 0 ? "+8%" : "0%"}
                                        trend="up"
                                    />
                                    <PremiumStatCard
                                        title="Verified"
                                        value={userStats.verified}
                                        icon={<VerifiedIcon />}
                                        color={palette.teal}
                                        gradient={`linear-gradient(135deg, ${palette.teal}, ${palette.tealDeep})`}
                                        delay={0.13}
                                        change={userStats.verified > 0 ? "+5%" : "0%"}
                                        trend="up"
                                    />
                                    <PremiumStatCard
                                        title="Growth Rate"
                                        value={userStats.growthRate}
                                        icon={<TrendingUpIcon />}
                                        color={palette.gold}
                                        gradient={`linear-gradient(135deg, ${palette.gold}, ${palette.goldDeep})`}
                                        delay={0.18}
                                        change={`${userStats.growthRate}%`}
                                        trend={userStats.growthRate > 5 ? 'up' : 'down'}
                                    />
                                </Box>

                                <SectionDivider 
                                    label="User Trends" 
                                    icon={<TimelineIcon />} 
                                    color={palette.purple} 
                                />

                                <Paper
                                    elevation={4}
                                    sx={{
                                        p: 3,
                                        borderRadius: '20px',
                                        border: `1px solid ${palette.hairline}`,
                                        boxShadow: `
                                            0 4px 24px rgba(25,57,78,0.06),
                                            0 2px 8px rgba(30,23,18,0.03)
                                        `,
                                        mb: 4,
                                        position: 'relative',
                                        overflow: 'hidden',
                                        '&:hover': {
                                            boxShadow: `
                                                0 8px 32px rgba(25,57,78,0.08),
                                                0 4px 12px rgba(25,57,78,0.04)
                                            `,
                                            borderColor: `${palette.purple}30`,
                                            transform: 'translateY(-2px)',
                                            transition: 'all 0.3s ease',
                                        }
                                    }}
                                >
                                    <Box sx={{ 
                                        position: 'absolute',
                                        top: -80,
                                        right: -80,
                                        width: 240,
                                        height: 240,
                                        borderRadius: '50%',
                                        background: `radial-gradient(circle, ${alpha(palette.purple, 0.06)}, transparent 70%)`,
                                        filter: 'blur(50px)',
                                        pointerEvents: 'none',
                                    }} />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Box>
                                            <Typography sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 700, fontSize: '1.1rem', color: palette.ink }}>
                                                User Registration Trends
                                            </Typography>
                                            <SectionRule color={palette.purple} width={30} />
                                        </Box>
                                        <Chip 
                                            label="Last 12 Months" 
                                            size="small" 
                                            sx={{ 
                                                bgcolor: `${palette.purple}10`, 
                                                color: palette.purple, 
                                                fontWeight: 600,
                                                border: `1px solid ${alpha(palette.purple, 0.06)}`,
                                            }} 
                                        />
                                    </Box>
                                    <Box sx={{ height: 250 }}>
                                        <Line data={userChartData} options={chartOptions} />
                                    </Box>
                                </Paper>

                                <SectionDivider 
                                    label="User Directory" 
                                    icon={<GroupIcon />} 
                                    color={palette.purple} 
                                />

                                <Paper
                                    elevation={4}
                                    sx={{
                                        borderRadius: '20px',
                                        border: `1px solid ${palette.hairline}`,
                                        boxShadow: `
                                            0 4px 24px rgba(25,57,78,0.06),
                                            0 2px 8px rgba(30,23,18,0.03)
                                        `,
                                        overflow: 'hidden',
                                        '&:hover': {
                                            boxShadow: `
                                                0 8px 32px rgba(25,57,78,0.08),
                                                0 4px 12px rgba(25,57,78,0.04)
                                            `,
                                            borderColor: `${palette.purple}30`,
                                            transition: 'all 0.3s ease',
                                        }
                                    }}
                                >
                                    <Box sx={{ 
                                        p: 2, 
                                        bgcolor: palette.canvasAlt,
                                        borderBottom: `1px solid ${palette.hairline}`,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                    }}>
                                        <Typography sx={{ fontWeight: 700, color: palette.ink, fontSize: '0.9rem' }}>
                                            User List {users.length > 0 && `(${users.length})`}
                                        </Typography>
                                        <TextField
                                            placeholder="Search users..."
                                            size="small"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            sx={{ maxWidth: 250 }}
                                            slotProps={{
                                                input: {
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <SearchIcon sx={{ color: palette.inkFaint, fontSize: 18 }} />
                                                        </InputAdornment>
                                                    ),
                                                    sx: {
                                                        borderRadius: '10px',
                                                        bgcolor: palette.paper,
                                                        '&:hover': { bgcolor: palette.paper },
                                                        fontSize: '0.8rem',
                                                    }
                                                }
                                            }}
                                        />
                                    </Box>
                                    <TableContainer>
                                        <Table>
                                            <TableHead sx={{ bgcolor: palette.canvas }}>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 700, color: palette.ink, fontSize: '0.75rem' }}>#</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, color: palette.ink, fontSize: '0.75rem' }}>User</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, color: palette.ink, fontSize: '0.75rem' }}>Email</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, color: palette.ink, fontSize: '0.75rem' }}>Role</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, color: palette.ink, fontSize: '0.75rem' }}>Status</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, color: palette.ink, fontSize: '0.75rem' }}>Joined</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {users
                                                    .filter(u => 
                                                        (u.displayName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                                                        (u.email || '').toLowerCase().includes(searchTerm.toLowerCase())
                                                    )
                                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                                    .map((user, index) => (
                                                        <TableRow 
                                                            key={user.id}
                                                            sx={{
                                                                '&:hover': {
                                                                    bgcolor: alpha(palette.purple, 0.03),
                                                                    cursor: 'pointer',
                                                                },
                                                                transition: 'all 0.2s ease',
                                                            }}
                                                            onClick={() => navigate(`/dashboard/users/${user.id}`)}
                                                        >
                                                            <TableCell sx={{ fontSize: '0.8rem' }}>{page * rowsPerPage + index + 1}</TableCell>
                                                            <TableCell>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                                    <Avatar 
                                                                        sx={{ 
                                                                            width: 34, 
                                                                            height: 34, 
                                                                            borderRadius: '10px',
                                                                            bgcolor: alpha(palette.purple, 0.12), 
                                                                            color: palette.purple,
                                                                            fontWeight: 700,
                                                                            fontSize: '0.8rem',
                                                                        }}
                                                                    >
                                                                        {user.displayName?.charAt(0) || 'U'}
                                                                    </Avatar>
                                                                    <Typography fontWeight={600} sx={{ fontSize: '0.85rem', color: palette.ink }}>
                                                                        {user.displayName || 'N/A'}
                                                                    </Typography>
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell sx={{ fontSize: '0.8rem', color: palette.inkSoft }}>{user.email || 'N/A'}</TableCell>
                                                            <TableCell>
                                                                <Chip 
                                                                    label={user.role || 'User'} 
                                                                    size="small" 
                                                                    sx={{ 
                                                                        fontSize: '0.6rem', 
                                                                        fontWeight: 600,
                                                                        bgcolor: alpha(palette.gold, 0.08),
                                                                        color: palette.gold,
                                                                        border: `1px solid ${alpha(palette.gold, 0.06)}`,
                                                                    }} 
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    label={user.isActive && !user.isBlocked ? 'Active' : 'Inactive'}
                                                                    size="small"
                                                                    sx={{
                                                                        fontSize: '0.6rem',
                                                                        fontWeight: 600,
                                                                        bgcolor: user.isActive && !user.isBlocked ? alpha(palette.pine, 0.10) : alpha(palette.clay, 0.10),
                                                                        color: user.isActive && !user.isBlocked ? palette.pine : palette.clay,
                                                                        border: `1px solid ${user.isActive && !user.isBlocked ? alpha(palette.pine, 0.06) : alpha(palette.clay, 0.06)}`,
                                                                    }}
                                                                />
                                                            </TableCell>
                                                            <TableCell sx={{ fontSize: '0.75rem', color: palette.inkSoft }}>
                                                                {user.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                {users.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                                                            <PeopleIcon sx={{ fontSize: 40, color: palette.inkFaint, opacity: 0.2 }} />
                                                            <Typography sx={{ color: palette.inkFaint, mt: 1 }}>No users found for this company</Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    <TablePagination
                                        rowsPerPageOptions={[5, 10, 25, 50]}
                                        component="div"
                                        count={users.filter(u => 
                                            (u.displayName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                                            (u.email || '').toLowerCase().includes(searchTerm.toLowerCase())
                                        ).length}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        onPageChange={(e, newPage) => setPage(newPage)}
                                        onRowsPerPageChange={(e) => {
                                            setRowsPerPage(parseInt(e.target.value, 10));
                                            setPage(0);
                                        }}
                                        sx={{
                                            borderTop: `1px solid ${palette.hairline}`,
                                            '& .MuiTablePagination-select': {
                                                borderRadius: '8px',
                                            }
                                        }}
                                    />
                                </Paper>
                            </Box>
                        </TabPanel>

                        {/* ==================== MEETINGS TAB ==================== */}
                        <TabPanel value={tabValue} index={1}>
                            <Box sx={{ p: { xs: 2, md: 3.5 } }}>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                        <Button
                                            variant="contained"
                                            startIcon={<FileDownloadIcon />}
                                            onClick={() => setExportDialogOpen(true)}
                                            sx={{
                                                borderRadius: '12px',
                                                textTransform: 'none',
                                                fontWeight: 700,
                                                fontSize: '0.8rem',
                                                py: 0.8,
                                                px: 3,
                                                background: `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`,
                                                boxShadow: `0 4px 16px ${alpha(palette.rust, 0.3)}`,
                                                '&:hover': {
                                                    boxShadow: `0 8px 24px ${alpha(palette.rust, 0.4)}`,
                                                    transform: 'translateY(-2px)',
                                                }
                                            }}
                                        >
                                            Generate Meeting Report
                                        </Button>
                                    </motion.div>
                                </Box>

                                <SectionDivider 
                                    label="Meeting Overview" 
                                    icon={<EventIcon />} 
                                    color={palette.rust} 
                                />

                                <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr 1fr' },
                                    gap: 2.5,
                                    mb: 4,
                                }}>
                                    <PremiumStatCard
                                        title="Total Meetings"
                                        value={meetingStats.total}
                                        icon={<EventIcon />}
                                        color={palette.rust}
                                        gradient={`linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`}
                                        delay={0.03}
                                        change={meetingStats.total > 0 ? "+15%" : "0%"}
                                        trend="up"
                                    />
                                    <PremiumStatCard
                                        title="Completed"
                                        value={meetingStats.completed}
                                        icon={<CheckCircleIcon />}
                                        color={palette.pine}
                                        gradient={`linear-gradient(135deg, ${palette.pine}, ${palette.pineDeep})`}
                                        delay={0.08}
                                        change={meetingStats.completed > 0 ? "+10%" : "0%"}
                                        trend="up"
                                    />
                                    <PremiumStatCard
                                        title="Pending"
                                        value={meetingStats.pending}
                                        icon={<ScheduleIcon />}
                                        color={palette.gold}
                                        gradient={`linear-gradient(135deg, ${palette.gold}, ${palette.goldDeep})`}
                                        delay={0.13}
                                        change={meetingStats.pending > 0 ? "-3%" : "0%"}
                                        trend="down"
                                    />
                                    <PremiumStatCard
                                        title="Completion Rate"
                                        value={meetingStats.completionRate}
                                        icon={<TrendingUpIcon />}
                                        color={palette.teal}
                                        gradient={`linear-gradient(135deg, ${palette.teal}, ${palette.tealDeep})`}
                                        delay={0.18}
                                        change={`${meetingStats.completionRate}%`}
                                        trend={meetingStats.completionRate > 50 ? 'up' : 'down'}
                                    />
                                </Box>

                                <SectionDivider 
                                    label="Meeting Analytics" 
                                    icon={<BarChartIcon />} 
                                    color={palette.rust} 
                                />

                                <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
                                    gap: 3,
                                    mb: 4,
                                }}>
                                    <Paper
                                        elevation={4}
                                        sx={{
                                            p: 3,
                                            borderRadius: '20px',
                                            border: `1px solid ${palette.hairline}`,
                                            boxShadow: `
                                                0 4px 24px rgba(25,57,78,0.06),
                                                0 2px 8px rgba(30,23,18,0.03)
                                            `,
                                            position: 'relative',
                                            overflow: 'hidden',
                                            '&:hover': {
                                                boxShadow: `
                                                    0 8px 32px rgba(25,57,78,0.08),
                                                    0 4px 12px rgba(25,57,78,0.04)
                                                `,
                                                borderColor: `${palette.rust}30`,
                                                transform: 'translateY(-2px)',
                                                transition: 'all 0.3s ease',
                                            }
                                        }}
                                    >
                                        <Box sx={{ 
                                            position: 'absolute',
                                            top: -80,
                                            right: -80,
                                            width: 240,
                                            height: 240,
                                            borderRadius: '50%',
                                            background: `radial-gradient(circle, ${alpha(palette.rust, 0.06)}, transparent 70%)`,
                                            filter: 'blur(50px)',
                                            pointerEvents: 'none',
                                        }} />
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Box>
                                                <Typography sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 700, fontSize: '1.1rem', color: palette.ink }}>
                                                    Meeting Trends
                                                </Typography>
                                                <SectionRule color={palette.rust} width={30} />
                                            </Box>
                                            <Chip 
                                                label="Last 12 Months" 
                                                size="small" 
                                                sx={{ 
                                                    bgcolor: `${palette.rust}10`, 
                                                    color: palette.rust, 
                                                    fontWeight: 600,
                                                    border: `1px solid ${alpha(palette.rust, 0.06)}`,
                                                }} 
                                            />
                                        </Box>
                                        <Box sx={{ height: 220 }}>
                                            <Line data={meetingChartData} options={chartOptions} />
                                        </Box>
                                    </Paper>
                                    <Paper
                                        elevation={4}
                                        sx={{
                                            p: 3,
                                            borderRadius: '20px',
                                            border: `1px solid ${palette.hairline}`,
                                            boxShadow: `
                                                0 4px 24px rgba(25,57,78,0.06),
                                                0 2px 8px rgba(30,23,18,0.03)
                                            `,
                                            position: 'relative',
                                            overflow: 'hidden',
                                            '&:hover': {
                                                boxShadow: `
                                                    0 8px 32px rgba(25,57,78,0.08),
                                                    0 4px 12px rgba(25,57,78,0.04)
                                                `,
                                                borderColor: `${palette.pine}30`,
                                                transform: 'translateY(-2px)',
                                                transition: 'all 0.3s ease',
                                            }
                                        }}
                                    >
                                        <Box sx={{ 
                                            position: 'absolute',
                                            top: -80,
                                            right: -80,
                                            width: 240,
                                            height: 240,
                                            borderRadius: '50%',
                                            background: `radial-gradient(circle, ${alpha(palette.pine, 0.06)}, transparent 70%)`,
                                            filter: 'blur(50px)',
                                            pointerEvents: 'none',
                                        }} />
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Box>
                                                <Typography sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 700, fontSize: '1.1rem', color: palette.ink }}>
                                                    Meeting Status
                                                </Typography>
                                                <SectionRule color={palette.pine} width={30} />
                                            </Box>
                                        </Box>
                                        <Box sx={{ height: 220 }}>
                                            <Doughnut data={meetingStatusData} options={doughnutOptions} />
                                        </Box>
                                    </Paper>
                                </Box>

                                <SectionDivider 
                                    label="Meeting Directory" 
                                    icon={<MeetingRoomIcon />} 
                                    color={palette.rust} 
                                />

                                <Paper
                                    elevation={4}
                                    sx={{
                                        borderRadius: '20px',
                                        border: `1px solid ${palette.hairline}`,
                                        boxShadow: `
                                            0 4px 24px rgba(25,57,78,0.06),
                                            0 2px 8px rgba(30,23,18,0.03)
                                        `,
                                        overflow: 'hidden',
                                        '&:hover': {
                                            boxShadow: `
                                                0 8px 32px rgba(25,57,78,0.08),
                                                0 4px 12px rgba(25,57,78,0.04)
                                            `,
                                            borderColor: `${palette.rust}30`,
                                            transition: 'all 0.3s ease',
                                        }
                                    }}
                                >
                                    <Box sx={{ 
                                        p: 2, 
                                        bgcolor: palette.canvasAlt,
                                        borderBottom: `1px solid ${palette.hairline}`,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                    }}>
                                        <Typography sx={{ fontWeight: 700, color: palette.ink, fontSize: '0.9rem' }}>
                                            Meeting List {meetings.length > 0 && `(${meetings.length})`}
                                        </Typography>
                                        <TextField
                                            placeholder="Search meetings..."
                                            size="small"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            sx={{ maxWidth: 250 }}
                                            slotProps={{
                                                input: {
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <SearchIcon sx={{ color: palette.inkFaint, fontSize: 18 }} />
                                                        </InputAdornment>
                                                    ),
                                                    sx: {
                                                        borderRadius: '10px',
                                                        bgcolor: palette.paper,
                                                        '&:hover': { bgcolor: palette.paper },
                                                        fontSize: '0.8rem',
                                                    }
                                                }
                                            }}
                                        />
                                    </Box>
                                    <TableContainer>
                                        <Table>
                                            <TableHead sx={{ bgcolor: palette.canvas }}>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 700, color: palette.ink, fontSize: '0.75rem' }}>#</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, color: palette.ink, fontSize: '0.75rem' }}>Title</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, color: palette.ink, fontSize: '0.75rem' }}>Status</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, color: palette.ink, fontSize: '0.75rem' }}>Scheduled</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, color: palette.ink, fontSize: '0.75rem' }}>Duration</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, color: palette.ink, fontSize: '0.75rem' }}>Participants</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {meetings
                                                    .filter(m => 
                                                        (m.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                                                        (m.status || '').toLowerCase().includes(searchTerm.toLowerCase())
                                                    )
                                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                                    .map((meeting, index) => (
                                                        <TableRow 
                                                            key={meeting.id}
                                                            sx={{
                                                                '&:hover': {
                                                                    bgcolor: alpha(palette.rust, 0.03),
                                                                    cursor: 'pointer',
                                                                },
                                                                transition: 'all 0.2s ease',
                                                            }}
                                                            onClick={() => navigate(`/dashboard/meetings/${meeting.id}`)}
                                                        >
                                                            <TableCell sx={{ fontSize: '0.8rem' }}>{page * rowsPerPage + index + 1}</TableCell>
                                                            <TableCell>
                                                                <Typography fontWeight={600} sx={{ fontSize: '0.85rem', color: palette.ink }}>
                                                                    {meeting.title || 'N/A'}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    label={meeting.status || 'N/A'}
                                                                    size="small"
                                                                    sx={{
                                                                        fontSize: '0.6rem',
                                                                        fontWeight: 600,
                                                                        bgcolor: meeting.status === 'completed' ? alpha(palette.pine, 0.10) :
                                                                                meeting.status === 'pending' ? alpha(palette.gold, 0.10) :
                                                                                alpha(palette.clay, 0.10),
                                                                        color: meeting.status === 'completed' ? palette.pine :
                                                                                meeting.status === 'pending' ? palette.gold :
                                                                                palette.clay,
                                                                        border: `1px solid ${meeting.status === 'completed' ? alpha(palette.pine, 0.06) :
                                                                                meeting.status === 'pending' ? alpha(palette.gold, 0.06) :
                                                                                alpha(palette.clay, 0.06)}`,
                                                                    }}
                                                                />
                                                            </TableCell>
                                                            <TableCell sx={{ fontSize: '0.8rem', color: palette.inkSoft }}>
                                                                {meeting.scheduledTime?.toDate?.()?.toLocaleDateString() || 'N/A'}
                                                            </TableCell>
                                                            <TableCell sx={{ fontSize: '0.8rem', color: palette.inkSoft }}>
                                                                {meeting.duration || 'N/A'} min
                                                            </TableCell>
                                                            <TableCell sx={{ fontSize: '0.8rem', color: palette.inkSoft }}>
                                                                {meeting.participants?.length || 0}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                {meetings.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                                                            <EventIcon sx={{ fontSize: 40, color: palette.inkFaint, opacity: 0.2 }} />
                                                            <Typography sx={{ color: palette.inkFaint, mt: 1 }}>No meetings found for this company</Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    <TablePagination
                                        rowsPerPageOptions={[5, 10, 25, 50]}
                                        component="div"
                                        count={meetings.filter(m => 
                                            (m.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                                            (m.status || '').toLowerCase().includes(searchTerm.toLowerCase())
                                        ).length}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        onPageChange={(e, newPage) => setPage(newPage)}
                                        onRowsPerPageChange={(e) => {
                                            setRowsPerPage(parseInt(e.target.value, 10));
                                            setPage(0);
                                        }}
                                        sx={{
                                            borderTop: `1px solid ${palette.hairline}`,
                                            '& .MuiTablePagination-select': {
                                                borderRadius: '8px',
                                            }
                                        }}
                                    />
                                </Paper>
                            </Box>
                        </TabPanel>

                        {/* ==================== INVITATIONS TAB ==================== */}
                        <TabPanel value={tabValue} index={2}>
                            <Box sx={{ p: { xs: 2, md: 3.5 } }}>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                        <Button
                                            variant="contained"
                                            startIcon={<FileDownloadIcon />}
                                            onClick={() => setExportDialogOpen(true)}
                                            sx={{
                                                borderRadius: '12px',
                                                textTransform: 'none',
                                                fontWeight: 700,
                                                fontSize: '0.8rem',
                                                py: 0.8,
                                                px: 3,
                                                background: `linear-gradient(135deg, ${palette.gold}, ${palette.goldDeep})`,
                                                boxShadow: `0 4px 16px ${alpha(palette.gold, 0.3)}`,
                                                '&:hover': {
                                                    boxShadow: `0 8px 24px ${alpha(palette.gold, 0.4)}`,
                                                    transform: 'translateY(-2px)',
                                                }
                                            }}
                                        >
                                            Generate Invitation Report
                                        </Button>
                                    </motion.div>
                                </Box>

                                <SectionDivider 
                                    label="Invitation Overview" 
                                    icon={<MailIcon />} 
                                    color={palette.gold} 
                                />

                                <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr 1fr' },
                                    gap: 2.5,
                                    mb: 4,
                                }}>
                                    <PremiumStatCard
                                        title="Total Invitations"
                                        value={invitationStats.total}
                                        icon={<MailIcon />}
                                        color={palette.gold}
                                        gradient={`linear-gradient(135deg, ${palette.gold}, ${palette.goldDeep})`}
                                        delay={0.03}
                                        change={invitationStats.total > 0 ? "+18%" : "0%"}
                                        trend="up"
                                    />
                                    <PremiumStatCard
                                        title="Accepted"
                                        value={invitationStats.accepted}
                                        icon={<CheckCircleIcon />}
                                        color={palette.pine}
                                        gradient={`linear-gradient(135deg, ${palette.pine}, ${palette.pineDeep})`}
                                        delay={0.08}
                                        change={invitationStats.accepted > 0 ? "+12%" : "0%"}
                                        trend="up"
                                    />
                                    <PremiumStatCard
                                        title="Pending"
                                        value={invitationStats.pending}
                                        icon={<ScheduleIcon />}
                                        color={palette.gold}
                                        gradient={`linear-gradient(135deg, ${palette.gold}, ${palette.goldDeep})`}
                                        delay={0.13}
                                        change={invitationStats.pending > 0 ? "-5%" : "0%"}
                                        trend="down"
                                    />
                                    <PremiumStatCard
                                        title="Acceptance Rate"
                                        value={invitationStats.acceptanceRate}
                                        icon={<TrendingUpIcon />}
                                        color={palette.teal}
                                        gradient={`linear-gradient(135deg, ${palette.teal}, ${palette.tealDeep})`}
                                        delay={0.18}
                                        change={`${invitationStats.acceptanceRate}%`}
                                        trend={invitationStats.acceptanceRate > 50 ? 'up' : 'down'}
                                    />
                                </Box>

                                <SectionDivider 
                                    label="Invitation Analytics" 
                                    icon={<BarChartIcon />} 
                                    color={palette.gold} 
                                />

                                <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
                                    gap: 3,
                                    mb: 4,
                                }}>
                                    <Paper
                                        elevation={4}
                                        sx={{
                                            p: 3,
                                            borderRadius: '20px',
                                            border: `1px solid ${palette.hairline}`,
                                            boxShadow: `
                                                0 4px 24px rgba(25,57,78,0.06),
                                                0 2px 8px rgba(30,23,18,0.03)
                                            `,
                                            position: 'relative',
                                            overflow: 'hidden',
                                            '&:hover': {
                                                boxShadow: `
                                                    0 8px 32px rgba(25,57,78,0.08),
                                                    0 4px 12px rgba(25,57,78,0.04)
                                                `,
                                                borderColor: `${palette.gold}30`,
                                                transform: 'translateY(-2px)',
                                                transition: 'all 0.3s ease',
                                            }
                                        }}
                                    >
                                        <Box sx={{ 
                                            position: 'absolute',
                                            top: -80,
                                            right: -80,
                                            width: 240,
                                            height: 240,
                                            borderRadius: '50%',
                                            background: `radial-gradient(circle, ${alpha(palette.gold, 0.06)}, transparent 70%)`,
                                            filter: 'blur(50px)',
                                            pointerEvents: 'none',
                                        }} />
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Box>
                                                <Typography sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 700, fontSize: '1.1rem', color: palette.ink }}>
                                                    Invitation Trends
                                                </Typography>
                                                <SectionRule color={palette.gold} width={30} />
                                            </Box>
                                            <Chip 
                                                label="Last 12 Months" 
                                                size="small" 
                                                sx={{ 
                                                    bgcolor: `${palette.gold}10`, 
                                                    color: palette.gold, 
                                                    fontWeight: 600,
                                                    border: `1px solid ${alpha(palette.gold, 0.06)}`,
                                                }} 
                                            />
                                        </Box>
                                        <Box sx={{ height: 220 }}>
                                            <Line data={invitationChartData} options={chartOptions} />
                                        </Box>
                                    </Paper>
                                    <Paper
                                        elevation={4}
                                        sx={{
                                            p: 3,
                                            borderRadius: '20px',
                                            border: `1px solid ${palette.hairline}`,
                                            boxShadow: `
                                                0 4px 24px rgba(25,57,78,0.06),
                                                0 2px 8px rgba(30,23,18,0.03)
                                            `,
                                            position: 'relative',
                                            overflow: 'hidden',
                                            '&:hover': {
                                                boxShadow: `
                                                    0 8px 32px rgba(25,57,78,0.08),
                                                    0 4px 12px rgba(25,57,78,0.04)
                                                `,
                                                borderColor: `${palette.gold}30`,
                                                transform: 'translateY(-2px)',
                                                transition: 'all 0.3s ease',
                                            }
                                        }}
                                    >
                                        <Box sx={{ 
                                            position: 'absolute',
                                            top: -80,
                                            right: -80,
                                            width: 240,
                                            height: 240,
                                            borderRadius: '50%',
                                            background: `radial-gradient(circle, ${alpha(palette.gold, 0.06)}, transparent 70%)`,
                                            filter: 'blur(50px)',
                                            pointerEvents: 'none',
                                        }} />
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Box>
                                                <Typography sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 700, fontSize: '1.1rem', color: palette.ink }}>
                                                    Invitation Status
                                                </Typography>
                                                <SectionRule color={palette.gold} width={30} />
                                            </Box>
                                        </Box>
                                        <Box sx={{ height: 220 }}>
                                            <Doughnut data={invitationStatusData} options={doughnutOptions} />
                                        </Box>
                                    </Paper>
                                </Box>

                                <SectionDivider 
                                    label="Invitation Directory" 
                                    icon={<MailIcon />} 
                                    color={palette.gold} 
                                />

                                <Paper
                                    elevation={4}
                                    sx={{
                                        borderRadius: '20px',
                                        border: `1px solid ${palette.hairline}`,
                                        boxShadow: `
                                            0 4px 24px rgba(25,57,78,0.06),
                                            0 2px 8px rgba(30,23,18,0.03)
                                        `,
                                        overflow: 'hidden',
                                        '&:hover': {
                                            boxShadow: `
                                                0 8px 32px rgba(25,57,78,0.08),
                                                0 4px 12px rgba(25,57,78,0.04)
                                            `,
                                            borderColor: `${palette.gold}30`,
                                            transition: 'all 0.3s ease',
                                        }
                                    }}
                                >
                                    <Box sx={{ 
                                        p: 2, 
                                        bgcolor: palette.canvasAlt,
                                        borderBottom: `1px solid ${palette.hairline}`,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                    }}>
                                        <Typography sx={{ fontWeight: 700, color: palette.ink, fontSize: '0.9rem' }}>
                                            Invitation List {invitations.length > 0 && `(${invitations.length})`}
                                        </Typography>
                                        <TextField
                                            placeholder="Search invitations..."
                                            size="small"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            sx={{ maxWidth: 250 }}
                                            slotProps={{
                                                input: {
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <SearchIcon sx={{ color: palette.inkFaint, fontSize: 18 }} />
                                                        </InputAdornment>
                                                    ),
                                                    sx: {
                                                        borderRadius: '10px',
                                                        bgcolor: palette.paper,
                                                        '&:hover': { bgcolor: palette.paper },
                                                        fontSize: '0.8rem',
                                                    }
                                                }
                                            }}
                                        />
                                    </Box>
                                    <TableContainer>
                                        <Table>
                                            <TableHead sx={{ bgcolor: palette.canvas }}>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 700, color: palette.ink, fontSize: '0.75rem' }}>#</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, color: palette.ink, fontSize: '0.75rem' }}>From</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, color: palette.ink, fontSize: '0.75rem' }}>To</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, color: palette.ink, fontSize: '0.75rem' }}>Status</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, color: palette.ink, fontSize: '0.75rem' }}>AI Score</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, color: palette.ink, fontSize: '0.75rem' }}>Sent</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {invitations
                                                    .filter(inv => 
                                                        (inv.companyName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                                                        (inv.targetCompanyName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                                                        (inv.status || '').toLowerCase().includes(searchTerm.toLowerCase())
                                                    )
                                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                                    .map((inv, index) => (
                                                        <TableRow 
                                                            key={inv.id}
                                                            sx={{
                                                                '&:hover': {
                                                                    bgcolor: alpha(palette.gold, 0.03),
                                                                    cursor: 'pointer',
                                                                },
                                                                transition: 'all 0.2s ease',
                                                            }}
                                                            onClick={() => navigate(`/dashboard/invitations/${inv.id}`)}
                                                        >
                                                            <TableCell sx={{ fontSize: '0.8rem' }}>{page * rowsPerPage + index + 1}</TableCell>
                                                            <TableCell>
                                                                <Typography fontWeight={600} sx={{ fontSize: '0.85rem', color: palette.ink }}>
                                                                    {inv.companyName || 'N/A'}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell sx={{ fontSize: '0.8rem', color: palette.inkSoft }}>
                                                                {inv.targetCompanyName || 'N/A'}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    label={inv.status || 'N/A'}
                                                                    size="small"
                                                                    sx={{
                                                                        fontSize: '0.6rem',
                                                                        fontWeight: 600,
                                                                        bgcolor: inv.status === 'accepted' ? alpha(palette.pine, 0.10) :
                                                                                inv.status === 'pending' ? alpha(palette.gold, 0.10) :
                                                                                alpha(palette.clay, 0.10),
                                                                        color: inv.status === 'accepted' ? palette.pine :
                                                                                inv.status === 'pending' ? palette.gold :
                                                                                palette.clay,
                                                                        border: `1px solid ${inv.status === 'accepted' ? alpha(palette.pine, 0.06) :
                                                                                inv.status === 'pending' ? alpha(palette.gold, 0.06) :
                                                                                alpha(palette.clay, 0.06)}`,
                                                                    }}
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    label={`${Math.round(inv.aiScore || 0)}%`}
                                                                    size="small"
                                                                    sx={{
                                                                        fontSize: '0.6rem',
                                                                        fontWeight: 600,
                                                                        bgcolor: (inv.aiScore || 0) >= 70 ? alpha(palette.pine, 0.10) :
                                                                                (inv.aiScore || 0) >= 40 ? alpha(palette.gold, 0.10) :
                                                                                alpha(palette.clay, 0.10),
                                                                        color: (inv.aiScore || 0) >= 70 ? palette.pine :
                                                                                (inv.aiScore || 0) >= 40 ? palette.gold :
                                                                                palette.clay,
                                                                        border: `1px solid ${(inv.aiScore || 0) >= 70 ? alpha(palette.pine, 0.06) :
                                                                                (inv.aiScore || 0) >= 40 ? alpha(palette.gold, 0.06) :
                                                                                alpha(palette.clay, 0.06)}`,
                                                                    }}
                                                                />
                                                            </TableCell>
                                                            <TableCell sx={{ fontSize: '0.75rem', color: palette.inkSoft }}>
                                                                {inv.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                {invitations.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                                                            <MailIcon sx={{ fontSize: 40, color: palette.inkFaint, opacity: 0.2 }} />
                                                            <Typography sx={{ color: palette.inkFaint, mt: 1 }}>No invitations found for this company</Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    <TablePagination
                                        rowsPerPageOptions={[5, 10, 25, 50]}
                                        component="div"
                                        count={invitations.filter(inv => 
                                            (inv.companyName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                                            (inv.targetCompanyName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                                            (inv.status || '').toLowerCase().includes(searchTerm.toLowerCase())
                                        ).length}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        onPageChange={(e, newPage) => setPage(newPage)}
                                        onRowsPerPageChange={(e) => {
                                            setRowsPerPage(parseInt(e.target.value, 10));
                                            setPage(0);
                                        }}
                                        sx={{
                                            borderTop: `1px solid ${palette.hairline}`,
                                            '& .MuiTablePagination-select': {
                                                borderRadius: '8px',
                                            }
                                        }}
                                    />
                                </Paper>
                            </Box>
                        </TabPanel>

                        {/* ==================== COMPANY TAB (Single Company) ==================== */}
                        <TabPanel value={tabValue} index={3}>
                            <Box sx={{ p: { xs: 2, md: 3.5 } }}>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                        <Button
                                            variant="contained"
                                            startIcon={<FileDownloadIcon />}
                                            onClick={() => setExportDialogOpen(true)}
                                            sx={{
                                                borderRadius: '12px',
                                                textTransform: 'none',
                                                fontWeight: 700,
                                                fontSize: '0.8rem',
                                                py: 0.8,
                                                px: 3,
                                                background: `linear-gradient(135deg, ${palette.pine}, ${palette.pineDeep})`,
                                                boxShadow: `0 4px 16px ${alpha(palette.pine, 0.3)}`,
                                                '&:hover': {
                                                    boxShadow: `0 8px 24px ${alpha(palette.pine, 0.4)}`,
                                                    transform: 'translateY(-2px)',
                                                }
                                            }}
                                        >
                                            Generate Company Report
                                        </Button>
                                    </motion.div>
                                </Box>

                                <SectionDivider 
                                    label="Company Profile" 
                                    icon={<BusinessIcon />} 
                                    color={palette.pine} 
                                />

                                <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr 1fr' },
                                    gap: 2.5,
                                    mb: 4,
                                }}>
                                    <PremiumStatCard
                                        title="Company Status"
                                        value={companyStats.active > 0 ? 1 : 0}
                                        icon={<CheckCircleIcon />}
                                        color={companyStats.active > 0 ? palette.pine : palette.clay}
                                        gradient={companyStats.active > 0 ? 
                                            `linear-gradient(135deg, ${palette.pine}, ${palette.pineDeep})` : 
                                            `linear-gradient(135deg, ${palette.clay}, ${palette.clayLight})`}
                                        delay={0.03}
                                        change={companyStats.active > 0 ? "Active" : "Inactive"}
                                        trend={companyStats.active > 0 ? 'up' : 'down'}
                                    />
                                    <PremiumStatCard
                                        title="Verified"
                                        value={companyStats.verified > 0 ? 1 : 0}
                                        icon={<VerifiedIcon />}
                                        color={companyStats.verified > 0 ? palette.teal : palette.inkFaint}
                                        gradient={companyStats.verified > 0 ? 
                                            `linear-gradient(135deg, ${palette.teal}, ${palette.tealDeep})` : 
                                            `linear-gradient(135deg, ${palette.inkFaint}, ${palette.inkSoft})`}
                                        delay={0.08}
                                        change={companyStats.verified > 0 ? "Verified" : "Not Verified"}
                                        trend={companyStats.verified > 0 ? 'up' : 'down'}
                                    />
                                    <PremiumStatCard
                                        title="Team Members"
                                        value={userStats.total}
                                        icon={<PeopleIcon />}
                                        color={palette.purple}
                                        gradient={`linear-gradient(135deg, ${palette.purple}, ${palette.navy})`}
                                        delay={0.13}
                                        change={`${userStats.total} users`}
                                        trend="up"
                                    />
                                    <PremiumStatCard
                                        title="Engagement"
                                        value={companyStats.engagementRate}
                                        icon={<TrendingUpIcon />}
                                        color={palette.gold}
                                        gradient={`linear-gradient(135deg, ${palette.gold}, ${palette.goldDeep})`}
                                        delay={0.18}
                                        change={`${companyStats.engagementRate}%`}
                                        trend={companyStats.engagementRate > 50 ? 'up' : 'down'}
                                    />
                                </Box>

                                <SectionDivider 
                                    label="Company Analytics" 
                                    icon={<BarChartIcon />} 
                                    color={palette.pine} 
                                />

                                <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                                    gap: 3,
                                    mb: 4,
                                }}>
                                    <Paper
                                        elevation={4}
                                        sx={{
                                            p: 3,
                                            borderRadius: '20px',
                                            border: `1px solid ${palette.hairline}`,
                                            boxShadow: `
                                                0 4px 24px rgba(25,57,78,0.06),
                                                0 2px 8px rgba(30,23,18,0.03)
                                            `,
                                            position: 'relative',
                                            overflow: 'hidden',
                                            '&:hover': {
                                                boxShadow: `
                                                    0 8px 32px rgba(25,57,78,0.08),
                                                    0 4px 12px rgba(25,57,78,0.04)
                                                `,
                                                borderColor: `${palette.pine}30`,
                                                transform: 'translateY(-2px)',
                                                transition: 'all 0.3s ease',
                                            }
                                        }}
                                    >
                                        <Box sx={{ 
                                            position: 'absolute',
                                            top: -80,
                                            right: -80,
                                            width: 240,
                                            height: 240,
                                            borderRadius: '50%',
                                            background: `radial-gradient(circle, ${alpha(palette.pine, 0.06)}, transparent 70%)`,
                                            filter: 'blur(50px)',
                                            pointerEvents: 'none',
                                        }} />
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Box>
                                                <Typography sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 700, fontSize: '1.1rem', color: palette.ink }}>
                                                    Company Status
                                                </Typography>
                                                <SectionRule color={palette.pine} width={30} />
                                            </Box>
                                        </Box>
                                        <Box sx={{ height: 220 }}>
                                            <Pie data={companyStatusData} options={pieOptions} />
                                        </Box>
                                    </Paper>
                                    <Paper
                                        elevation={4}
                                        sx={{
                                            p: 3,
                                            borderRadius: '20px',
                                            border: `1px solid ${palette.hairline}`,
                                            boxShadow: `
                                                0 4px 24px rgba(25,57,78,0.06),
                                                0 2px 8px rgba(30,23,18,0.03)
                                            `,
                                            position: 'relative',
                                            overflow: 'hidden',
                                            '&:hover': {
                                                boxShadow: `
                                                    0 8px 32px rgba(25,57,78,0.08),
                                                    0 4px 12px rgba(25,57,78,0.04)
                                                `,
                                                borderColor: `${palette.pine}30`,
                                                transform: 'translateY(-2px)',
                                                transition: 'all 0.3s ease',
                                            }
                                        }}
                                    >
                                        <Box sx={{ 
                                            position: 'absolute',
                                            top: -80,
                                            right: -80,
                                            width: 240,
                                            height: 240,
                                            borderRadius: '50%',
                                            background: `radial-gradient(circle, ${alpha(palette.pine, 0.06)}, transparent 70%)`,
                                            filter: 'blur(50px)',
                                            pointerEvents: 'none',
                                        }} />
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Box>
                                                <Typography sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 700, fontSize: '1.1rem', color: palette.ink }}>
                                                    Company Summary
                                                </Typography>
                                                <SectionRule color={palette.pine} width={30} />
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, justifyContent: 'center', height: 220 }}>
                                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                                                <Box sx={{ 
                                                    textAlign: 'center',
                                                    p: 2,
                                                    borderRadius: '12px',
                                                    bgcolor: alpha(palette.pine, 0.04),
                                                    border: `1px solid ${alpha(palette.pine, 0.06)}`,
                                                }}>
                                                    <Typography variant="caption" sx={{ color: palette.inkSoft, fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                                        Name
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: palette.ink, mt: 0.5 }}>
                                                        {getCompanyName()}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ 
                                                    textAlign: 'center',
                                                    p: 2,
                                                    borderRadius: '12px',
                                                    bgcolor: alpha(palette.pine, 0.04),
                                                    border: `1px solid ${alpha(palette.pine, 0.06)}`,
                                                }}>
                                                    <Typography variant="caption" sx={{ color: palette.inkSoft, fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                                        Users
                                                    </Typography>
                                                    <Typography sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontSize: '1.8rem', fontWeight: 700, color: palette.ink }}>
                                                        {userStats.total}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ 
                                                    textAlign: 'center',
                                                    p: 2,
                                                    borderRadius: '12px',
                                                    bgcolor: alpha(palette.pine, 0.04),
                                                    border: `1px solid ${alpha(palette.pine, 0.06)}`,
                                                }}>
                                                    <Typography variant="caption" sx={{ color: palette.inkSoft, fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                                        Meetings
                                                    </Typography>
                                                    <Typography sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontSize: '1.8rem', fontWeight: 700, color: palette.rust }}>
                                                        {meetingStats.total}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Box sx={{ 
                                                textAlign: 'center',
                                                p: 2,
                                                borderRadius: '12px',
                                                bgcolor: alpha(palette.gold, 0.04),
                                                border: `1px solid ${alpha(palette.gold, 0.06)}`,
                                            }}>
                                                <Typography variant="caption" sx={{ color: palette.inkSoft, fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                                    Invitations
                                                </Typography>
                                                <Typography sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontSize: '1.8rem', fontWeight: 700, color: palette.gold }}>
                                                    {invitationStats.total}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Box>
                            </Box>
                        </TabPanel>
                    </Paper>
                </motion.div>

                {/* Export Dialog */}
                <Dialog
                    open={exportDialogOpen}
                    onClose={() => setExportDialogOpen(false)}
                    maxWidth="sm"
                    fullWidth
                    slotProps={{ paper: {
                        sx: {
                            borderRadius: '24px',
                            border: `1px solid ${palette.hairlineStrong}`,
                            boxShadow: '0 24px 80px rgba(25,57,78,0.10)',
                            background: palette.paper,
                            overflow: 'hidden',
                        }
                    } }}
                >
                    <DialogTitle sx={{
                        borderBottom: `1px solid ${palette.hairline}`,
                        pb: 2.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: palette.canvasAlt,
                        fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                        fontWeight: 700,
                        color: palette.ink,
                    }}>
                        <Box sx={{ alignItems: 'center', gap: 2, display: 'flex' }}>
                            <Box sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`,
                                color: '#fff',
                                boxShadow: `0 4px 16px ${alpha(palette.rust, 0.25)}`,
                            }}>
                                <FileDownloadIcon />
                            </Box>
                            <Box>
                                <Typography variant="h6" fontWeight={700} sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontSize: '1.1rem' }}>
                                    Export Report
                                </Typography>
                                <Typography variant="caption" sx={{ color: palette.inkSoft, fontSize: '0.7rem' }}>
                                    Export report for {getCompanyName()}
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton onClick={() => setExportDialogOpen(false)} sx={{ color: palette.inkFaint }}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>

                    <DialogContent sx={{ pt: 3.5, px: 3 }}>
                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    label="Report Name"
                                    value={reportName}
                                    onChange={(e) => setReportName(e.target.value)}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '12px',
                                            bgcolor: palette.canvas,
                                            '&:hover': { bgcolor: palette.paper },
                                            '&.Mui-focused': { bgcolor: palette.paper },
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid size={{ xs: 12 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Export Format</InputLabel>
                                    <Select
                                        value={downloadFormat}
                                        onChange={(e) => setDownloadFormat(e.target.value)}
                                        label="Export Format"
                                        sx={{
                                            borderRadius: '12px',
                                            bgcolor: palette.canvas,
                                            '&:hover': { bgcolor: palette.paper },
                                            '&.Mui-focused': { bgcolor: palette.paper },
                                        }}
                                    >
                                        <MenuItem value="pdf">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <PdfIcon sx={{ color: palette.rust }} />
                                                <Typography>PDF Document</Typography>
                                            </Box>
                                        </MenuItem>
                                        <MenuItem value="excel">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <TableChartIcon sx={{ color: palette.pine }} />
                                                <Typography>Excel Spreadsheet</Typography>
                                            </Box>
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid size={{ xs: 12 }}>
                                <Alert
                                    severity="info"
                                    sx={{ 
                                        borderRadius: '12px', 
                                        bgcolor: alpha(palette.rust, 0.04),
                                        border: `1px solid ${alpha(palette.rust, 0.06)}`,
                                    }}
                                    icon={<InfoIcon />}
                                >
                                    <Typography variant="body2" sx={{ color: palette.inkSoft, fontSize: '0.85rem' }}>
                                        {downloadFormat === 'pdf' ? 
                                            'PDF report includes professional formatting with charts, tables, and statistics for the selected tab.' : 
                                            'Excel report includes all data across multiple sheets with full details for each section.'}
                                    </Typography>
                                </Alert>
                            </Grid>
                        </Grid>
                    </DialogContent>

                    <DialogActions sx={{ p: 3, borderTop: `1px solid ${palette.hairline}`, gap: 1.5, bgcolor: palette.canvasAlt }}>
                        <Button
                            onClick={() => setExportDialogOpen(false)}
                            sx={{ color: palette.inkSoft, fontWeight: 600, textTransform: 'none', fontSize: '0.8rem' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<EmailIcon />}
                            onClick={() => {
                                setExportDialogOpen(false);
                                setEmailDialogOpen(true);
                            }}
                            sx={{
                                borderRadius: '10px',
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '0.8rem',
                                borderColor: palette.teal,
                                color: palette.teal,
                                '&:hover': {
                                    bgcolor: alpha(palette.teal, 0.06),
                                    borderColor: palette.teal,
                                    transform: 'translateY(-2px)',
                                }
                            }}
                        >
                            Email Report
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={generatingReport ? <CircularProgress size={20} color="inherit" /> : downloadFormat === 'pdf' ? <PdfIcon /> : <TableChartIcon />}
                            onClick={downloadFormat === 'pdf' ? generatePDFReport : generateExcelReport}
                            disabled={generatingReport}
                            sx={{
                                borderRadius: '10px',
                                textTransform: 'none',
                                fontWeight: 700,
                                fontSize: '0.8rem',
                                py: 1.2,
                                px: 4,
                                background: `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`,
                                boxShadow: '0 4px 16px rgba(196,90,38,0.3)',
                                '&:hover': {
                                    boxShadow: '0 8px 24px rgba(196,90,38,0.4)',
                                    transform: 'translateY(-2px)',
                                },
                                '&:disabled': {
                                    background: alpha(palette.inkSoft, 0.2),
                                    boxShadow: 'none',
                                }
                            }}
                        >
                            {generatingReport ? 'Generating...' : `Download ${downloadFormat.toUpperCase()}`}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Email Report Dialog */}
                <Dialog
                    open={emailDialogOpen}
                    onClose={() => setEmailDialogOpen(false)}
                    maxWidth="sm"
                    fullWidth
                    slotProps={{ paper: {
                        sx: {
                            borderRadius: '24px',
                            border: `1px solid ${palette.hairlineStrong}`,
                            boxShadow: '0 24px 80px rgba(25,57,78,0.10)',
                            background: palette.paper,
                            overflow: 'hidden',
                        }
                    } }}
                >
                    <DialogTitle sx={{
                        borderBottom: `1px solid ${palette.hairline}`,
                        pb: 2.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: palette.canvasAlt,
                        fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                        fontWeight: 700,
                        color: palette.ink,
                    }}>
                        <Box sx={{ alignItems: 'center', gap: 2, display: 'flex' }}>
                            <Box sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: `linear-gradient(135deg, ${palette.teal}, ${palette.tealDeep})`,
                                color: '#fff',
                                boxShadow: `0 4px 16px ${alpha(palette.teal, 0.25)}`,
                            }}>
                                <EmailIcon />
                            </Box>
                            <Box>
                                <Typography variant="h6" fontWeight={700} sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontSize: '1.1rem' }}>
                                    Email Report
                                </Typography>
                                <Typography variant="caption" sx={{ color: palette.inkSoft, fontSize: '0.7rem' }}>
                                    Send the report for {getCompanyName()}
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton onClick={() => setEmailDialogOpen(false)} sx={{ color: palette.inkFaint }}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>

                    <DialogContent sx={{ pt: 3.5, px: 3 }}>
                        <Grid container spacing={2.5}>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    label="Email Address"
                                    type="email"
                                    value={emailAddress}
                                    onChange={(e) => setEmailAddress(e.target.value)}
                                    placeholder="recipient@company.com"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '12px',
                                            bgcolor: palette.canvas,
                                            '&:hover': { bgcolor: palette.paper },
                                            '&.Mui-focused': { bgcolor: palette.paper },
                                        }
                                    }}
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <EmailIcon sx={{ color: palette.inkSoft }} />
                                                </InputAdornment>
                                            ),
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Alert
                                    severity="info"
                                    sx={{ 
                                        borderRadius: '12px', 
                                        bgcolor: alpha(palette.teal, 0.04),
                                        border: `1px solid ${alpha(palette.teal, 0.06)}`,
                                    }}
                                    icon={<InfoIcon />}
                                >
                                    <Typography variant="body2" sx={{ color: palette.inkSoft, fontSize: '0.85rem' }}>
                                        The report will be sent as a {downloadFormat.toUpperCase()} attachment to the specified email address.
                                        Delivery may take a few minutes.
                                    </Typography>
                                </Alert>
                            </Grid>
                        </Grid>
                    </DialogContent>

                    <DialogActions sx={{ p: 3, borderTop: `1px solid ${palette.hairline}`, gap: 1.5, bgcolor: palette.canvasAlt }}>
                        <Button
                            onClick={() => setEmailDialogOpen(false)}
                            sx={{ color: palette.inkSoft, fontWeight: 600, textTransform: 'none', fontSize: '0.8rem' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={generatingReport ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                            onClick={() => {
                                setEmailDialogOpen(false);
                                toast.success(`Report sent to ${emailAddress}`);
                            }}
                            disabled={generatingReport || !emailAddress}
                            sx={{
                                borderRadius: '10px',
                                textTransform: 'none',
                                fontWeight: 700,
                                fontSize: '0.8rem',
                                py: 1.2,
                                px: 4,
                                background: `linear-gradient(135deg, ${palette.teal}, ${palette.tealDeep})`,
                                boxShadow: '0 4px 16px rgba(26,107,115,0.3)',
                                '&:hover': {
                                    boxShadow: '0 8px 24px rgba(26,107,115,0.4)',
                                    transform: 'translateY(-2px)',
                                },
                                '&:disabled': {
                                    background: alpha(palette.inkSoft, 0.2),
                                    boxShadow: 'none',
                                }
                            }}
                        >
                            {generatingReport ? 'Sending...' : 'Send Report'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </Box>
    );
};

export default Reporting;